import React, { useState, useEffect, useCallback, useRef } from 'react';
import { pathwayApi } from '../api';
import pathwayEmblems from '../assets/pathway-emblems.png';
import pathwayHomeOrbit from '../assets/pathway-home-orbit.png';
import pathwayRowFrame from '../assets/pathway-row-frame.png';
import pathwayConstellationAtlas from '../assets/pathway-constellation-atlas.png';
import { useAppNavigation, useAppBackHandler } from '../navigation/AppNavigation';

interface DimensionItem {
  key: string;
  label: string;
  name: string;
  icon: string;
  color: string;
  weight: number;
  exp?: number;
}

interface Pathway {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  popularity: number;
  difficulty: string;
  follower_count: number;
  sequences: Sequence[];
  dimension_profile?: DimensionItem[];
  primary_dimension?: DimensionItem | null;
  dimension_summary?: string;
}

type VerificationType = 'quiz' | 'practice' | 'reflection' | 'milestone' | 'streak';

interface VerificationConfig {
  required_days?: number;
  min_words?: number;
  require_evidence?: boolean;
  prompt?: string;
}

interface Sequence {
  level: number;
  name: string;
  title: string;
  description: string;
  exp_required: number;
  quiz_time_limit: number;
  verification_type?: VerificationType;
  verification_config?: VerificationConfig;
}

interface Progress {
  pathway_id: string;
  current_sequence: number;
  total_exp: number;
  quizzes_completed: number;
  streak_days?: number;
  last_completed_date?: string | null;
  can_upgrade?: boolean;
  next_level?: number | null;
  next_level_exp_required?: number | null;
  current_level_exp_required?: number;
  exp_progress?: number;
  exp_gap?: number;
  progress_percent?: number;
  quiz_time_limit_seconds?: number | null;
  verification_type?: VerificationType | null;
  verification_config?: VerificationConfig;
}

interface VerificationInfo {
  pathway_id: string;
  sequence_level: number;
  sequence_name?: string;
  verification_type: VerificationType | null;
  verification_config: VerificationConfig;
  completed_days: number;
  weekly_completed: number;
  required_days: number;
  progress_count?: number;
  can_pass?: boolean;
  can_upgrade?: boolean;
}

interface QuizData {
  pathway_id: string;
  sequence_level: number;
  sequence_name: string;
  questions: { id: number; question: string; options: string[] }[];
  pass_percent: number;
  total_questions: number;
  quiz_token: string;
  time_limit_seconds: number;
  issued_at: string;
  expires_at: string;
}

interface QuizResult {
  score: number;
  total_questions: number;
  score_percent: number;
  passed: boolean;
  exp_gained: number;
  time_limit_seconds: number;
  time_spent_seconds: number;
  within_time_limit: boolean;
  new_sequence?: number;
  progress?: Progress;
}

interface DailyTaskItem {
  id: number;
  title: string;
  description?: string;
  category?: string;
  exp: number;
  status: 'pending' | 'completed';
  completed_at?: string | null;
  added_to_daily?: boolean;
}

interface DailyTasksPayload {
  id: number;
  pathway_id: string;
  date: string;
  sequence_level: number;
  items: DailyTaskItem[];
}

type ViewState =
  | { type: 'list' }
  | { type: 'detail'; pathwayId: string }
  | { type: 'quiz'; pathwayId: string; level: number }
  | { type: 'quiz-result'; pathwayId: string; result: QuizResult }
  | { type: 'level-up'; pathwayId: string; result: QuizResult }
  | { type: 'verify'; pathwayId: string; level: number }
;

const VERIFICATION_META: Record<VerificationType, { label: string; icon: string; verb: string; desc: string }> = {
  quiz: { label: '限时答题', icon: '📝', verb: '进入考核', desc: '回答知识题，正确率达标即可晋升' },
  practice: { label: '实践打卡', icon: '🔥', verb: '确认达标', desc: '坚持完成每日任务，积累修行天数' },
  streak: { label: '连续坚持', icon: '📅', verb: '确认达标', desc: '保持连续打卡，养成稳定习惯' },
  reflection: { label: '反思自评', icon: '✍️', verb: '提交反思', desc: '写下你的实践与体会，达到字数即可晋升' },
  milestone: { label: '里程碑成就', icon: '🏔️', verb: '确认完成', desc: '完成一项真实成就，勾选确认即可晋升' },
};

interface Props {
  onClose: () => void;
  initialPathwayId?: string;
}

const PathOfAscension: React.FC<Props> = ({ onClose, initialPathwayId }) => {
  const navigation = useAppNavigation();
  const [pathways, setPathways] = useState<Pathway[]>([]);
  const [progressMap, setProgressMap] = useState<Record<string, Progress>>({});
  const [view, setView] = useState<ViewState>(() => initialPathwayId ? { type: 'detail', pathwayId: initialPathwayId } : { type: 'list' });
  const [loading, setLoading] = useState(true);
  const [quizData, setQuizData] = useState<QuizData | null>(null);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [quizRemainingSeconds, setQuizRemainingSeconds] = useState(0);
  // 始终指向最新的 submitQuiz，供倒计时归零时自动提交使用（避免闭包捕获到旧的空答案）
  const submitQuizRef = useRef<(opts?: { autoTimeout?: boolean }) => void>(() => {});

  const [dailyTasks, setDailyTasks] = useState<DailyTasksPayload | null>(null);
  const [dailyTasksLoading, setDailyTasksLoading] = useState(false);

  // 两个选项卡：mine=我的途径，all=所有途径
  const [activeTab, setActiveTab] = useState<'mine' | 'all'>('mine');

  // 订阅相关
  const [subscribedIds, setSubscribedIds] = useState<Set<string>>(new Set());
  const [subscribeLoading, setSubscribeLoading] = useState<string | null>(null);
  const [subscriptionsData, setSubscriptionsData] = useState<any[]>([]);

  // 当前途径详情页的每周任务
  const [weeklyTasks, setWeeklyTasks] = useState<any>(null);
  const [weeklyTasksLoading, setWeeklyTasksLoading] = useState(false);

  // 个性化升级考核（非答题）
  const [verificationInfo, setVerificationInfo] = useState<VerificationInfo | null>(null);
  const [reflectionText, setReflectionText] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');

  // 精细化 loading 状态：key 格式 daily_<taskId> / weekly_<taskId> / quiz_submit
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const setItemLoading = (key: string, val: boolean) =>
    setLoadingMap(prev => ({ ...prev, [key]: val }));
  const isItemLoading = (key: string) => !!loadingMap[key];

  // Toast 通知
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // 轻量激励：播报 streak
  const showStreakToast = (pid: string, nextProgress?: Progress) => {
    if (!nextProgress) return;
    const streak = nextProgress.streak_days ?? 0;
    if (streak > 0) {
      showToast(`连续完成 ${streak} 天 · ${getSequenceName(pid, nextProgress.current_sequence ?? 9)}`);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [pathwaysRes, progressRes, subsRes] = await Promise.allSettled([
        pathwayApi.getAllPathways(),
        pathwayApi.getAllProgress(),
        pathwayApi.getSubscriptions(),
      ]);

      if (pathwaysRes.status !== 'fulfilled') {
        throw pathwaysRes.reason;
      }

      setPathways(pathwaysRes.value.data);

      const pMap: Record<string, Progress> = {};
      if (progressRes.status === 'fulfilled') {
        (progressRes.value.data as Progress[]).forEach(p => { pMap[p.pathway_id] = p; });
      } else {
        console.error('加载途径进度失败:', progressRes.reason);
      }
      setProgressMap(pMap);

      const subsData = subsRes.status === 'fulfilled' ? (subsRes.value.data as any[]) : [];
      if (subsRes.status !== 'fulfilled') {
        console.error('加载途径订阅失败:', subsRes.reason);
      }
      setSubscribedIds(new Set<string>(subsData.map((s: any) => String(s.pathway_id))));
      setSubscriptionsData(subsData);
      if (subsData.length === 0) {
        setActiveTab('all');
      }
    } catch (err) {
      console.error('加载途径数据失败:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (view.type !== 'quiz' || !quizData?.time_limit_seconds) {
      setQuizRemainingSeconds(0);
      return;
    }
    setQuizRemainingSeconds(quizData.time_limit_seconds);
    const timer = window.setInterval(() => {
      setQuizRemainingSeconds(prev => {
        if (prev <= 1) {
          window.clearInterval(timer);
          // 超时自动提交（若已在提交中则跳过）。通过 ref 调用最新版本，确保提交已作答的答案。
          setTimeout(() => {
            if (!isItemLoading('quiz_submit')) submitQuizRef.current({ autoTimeout: true });
          }, 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.type, quizData]);

  // 当进入途径详情页时，自动拉取今日任务 + 每周任务
  useEffect(() => {
    if (view.type === 'detail') {
      const pid = view.pathwayId;
      loadTodayDailyTasks(pid);
      if (subscribedIds.has(pid)) {
        loadWeeklyTasks(pid);
      } else {
        setWeeklyTasks(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.type, (view as any).pathwayId, subscribedIds]);

  const loadQuiz = async (pathwayId: string, level: number) => {
    try {
      setLoading(true);
      const res = await pathwayApi.getQuiz(pathwayId, level);
      setQuizData(res.data);
      setAnswers({});
      setQuizRemainingSeconds(res.data.time_limit_seconds ?? 0);
      setView({ type: 'quiz', pathwayId, level });
    } catch (err: any) {
      showToast(err.response?.data?.detail || '加载题目失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 根据当前序列的考核方式，进入对应的晋升流程
  const startVerification = async (pathwayId: string, level: number, vtype: VerificationType) => {
    if (vtype === 'quiz') {
      loadQuiz(pathwayId, level);
      return;
    }
    // 非答题：拉取达标进度并打开个性化考核界面
    try {
      setLoading(true);
      const res = await pathwayApi.getVerificationInfo(pathwayId);
      setVerificationInfo(res.data);
      setReflectionText('');
      setEvidenceUrl('');
      setView({ type: 'verify', pathwayId, level });
    } catch (err: any) {
      showToast(err.response?.data?.detail || '加载考核信息失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const submitVerification = async (pathwayId: string, level: number, vtype: VerificationType) => {
    setItemLoading('verify_submit', true);
    try {
      const payload: { sequence_level: number; reflection?: string; milestone_confirmed?: boolean; evidence_url?: string } = {
        sequence_level: level,
      };
      if (vtype === 'reflection') payload.reflection = reflectionText.trim();
      if (vtype === 'milestone') {
        payload.milestone_confirmed = true;
        if (evidenceUrl.trim()) payload.evidence_url = evidenceUrl.trim();
      }
      const res = await pathwayApi.submitVerification(pathwayId, payload);
      const result: QuizResult = {
        score: 1,
        total_questions: 1,
        score_percent: 100,
        passed: res.data.passed,
        exp_gained: res.data.exp_gained ?? 0,
        time_limit_seconds: 0,
        time_spent_seconds: 0,
        within_time_limit: true,
        new_sequence: res.data.new_sequence,
        progress: res.data.progress,
      };
      const viewType = res.data.passed && res.data.new_sequence != null ? 'level-up' : 'quiz-result';
      setView({ type: viewType, pathwayId, result });
      if (res.data.progress) {
        setProgressMap(prev => ({ ...prev, [pathwayId]: res.data.progress }));
      }
    } catch (err: any) {
      showToast(err.response?.data?.detail || '提交失败', 'error');
    } finally {
      setItemLoading('verify_submit', false);
    }
  };

  const loadTodayDailyTasks = async (pathwayId: string) => {
    try {
      setDailyTasksLoading(true);
      const res = await pathwayApi.getTodayDailyTasks(pathwayId);
      setDailyTasks(res.data);
    } catch (err: any) {
      console.error('加载每日任务失败:', err);
      setDailyTasks(null);
    } finally {
      setDailyTasksLoading(false);
    }
  };

  const completeDailyTask = async (pathwayId: string, taskId: number) => {
    const key = `daily_${taskId}`;
    try {
      setItemLoading(key, true);
      const res = await pathwayApi.completeDailyTask(pathwayId, taskId);
      showToast(res.data.can_upgrade ? `任务完成！获得 ${res.data.exp_gained} EXP，已解锁升级考核` : `任务完成！获得 ${res.data.exp_gained} EXP`);
      // 直接用返回值更新本地状态，无需完整刷新
      if (res.data.daily_tasks) setDailyTasks(res.data.daily_tasks);
      if (res.data.progress) {
        setProgressMap(prev => ({ ...prev, [pathwayId]: res.data.progress }));
        showStreakToast(pathwayId, res.data.progress);
      }
    } catch (err: any) {
      showToast(err.response?.data?.detail || '完成失败', 'error');
    } finally {
      setItemLoading(key, false);
    }
  };

  const addDailyTask = async (pathwayId: string, taskId: number) => {
    const key = `add_daily_${taskId}`;
    try {
      setItemLoading(key, true);
      const res = await pathwayApi.addDailyTask(pathwayId, taskId);
      setDailyTasks(res.data.daily_tasks);
      showToast(res.data.added ? '已加入今日任务' : '这项任务已在今日清单中');
    } catch (err: any) {
      showToast(err.response?.data?.detail || '加入今日任务失败', 'error');
    } finally {
      setItemLoading(key, false);
    }
  };

  const submitQuiz = async (opts?: { autoTimeout?: boolean }) => {
    if (!quizData) return;
    if (isItemLoading('quiz_submit')) return;
    setItemLoading('quiz_submit', true);
    try {
      const res = await pathwayApi.submitQuiz({
        pathway_id: quizData.pathway_id,
        sequence_level: quizData.sequence_level,
        answers,
        quiz_token: quizData.quiz_token,
      });
      const viewType = res.data.passed && res.data.new_sequence != null ? 'level-up' : 'quiz-result';
      setView({ type: viewType, pathwayId: quizData.pathway_id, result: res.data });
      if (res.data.progress && res.data.pathway_id) {
        setProgressMap(prev => ({
          ...prev,
          [res.data.pathway_id]: res.data.progress,
        }));
      }
    } catch (err: any) {
      // 超时后后端会拒绝过期凭证；此时合成一个“超时未通过”的结果页，避免用户只看到一闪而过的提示
      if (opts?.autoTimeout) {
        const total = quizData.questions.length;
        const answered = quizData.questions.filter(q => answers[String(q.id)] !== undefined).length;
        setView({
          type: 'quiz-result',
          pathwayId: quizData.pathway_id,
          result: {
            score: 0,
            total_questions: total,
            score_percent: total ? Math.round((answered / total) * 100) : 0,
            passed: false,
            exp_gained: 0,
            time_limit_seconds: quizData.time_limit_seconds,
            time_spent_seconds: quizData.time_limit_seconds,
            within_time_limit: false,
          },
        });
      } else {
        showToast(err.response?.data?.detail || '提交失败', 'error');
      }
    } finally {
      setItemLoading('quiz_submit', false);
    }
  };

  // 让倒计时里的 ref 始终指向最新的 submitQuiz
  useEffect(() => {
    submitQuizRef.current = submitQuiz;
  });

  const toggleSubscribe = async (pathwayId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSubscribeLoading(pathwayId);
    try {
      if (subscribedIds.has(pathwayId)) {
        await pathwayApi.unsubscribe(pathwayId);
        const nextSubscribedIds = new Set<string>(Array.from(subscribedIds));
        nextSubscribedIds.delete(pathwayId);
        setSubscribedIds(nextSubscribedIds);
        if (nextSubscribedIds.size === 0) setActiveTab('all');
        setSubscriptionsData(prev => prev.filter(s => s.pathway_id !== pathwayId));
        showToast('已取消选择此途径');
      } else {
        await pathwayApi.subscribe(pathwayId);
        setSubscribedIds(prev => new Set(Array.from(prev).concat(pathwayId)));
        showToast('已选择途径，本周任务已生成');
        // 重新拉取完整订阅数据（含本周任务进度）
        pathwayApi.getSubscriptions().then(r => setSubscriptionsData(r.data)).catch(() => {});
      }
    } catch (err: any) {
      showToast(err.response?.data?.detail || '操作失败', 'error');
    } finally {
      setSubscribeLoading(null);
    }
  };

  const loadWeeklyTasks = async (pathwayId: string) => {
    setWeeklyTasksLoading(true);
    try {
      const res = await pathwayApi.getWeeklyTasks(pathwayId);
      setWeeklyTasks(res.data);
    } catch {
      setWeeklyTasks(null);
    } finally {
      setWeeklyTasksLoading(false);
    }
  };

  const completeWeeklyTask = async (pathwayId: string, taskId: number) => {
    const key = `weekly_${taskId}`;
    try {
      setItemLoading(key, true);
      const res = await pathwayApi.completeWeeklyTask(pathwayId, taskId);
      showToast(res.data.can_upgrade ? `本周任务完成！获得 ${res.data.exp_gained} EXP，已解锁升级考核` : `本周任务完成！获得 ${res.data.exp_gained} EXP`);
      // 局部更新周任务状态
      if (weeklyTasks) {
        setWeeklyTasks((prev: any) => ({
          ...prev,
          items: prev.items.map((t: any) =>
            String(t.id) === String(taskId)
              ? { ...t, status: 'completed', completed_at: new Date().toISOString() }
              : t
          ),
        }));
      }
      if (res.data.progress) {
        setProgressMap(prev => ({ ...prev, [pathwayId]: res.data.progress }));
        showStreakToast(pathwayId, res.data.progress);
      }
    } catch (err: any) {
      showToast(err.response?.data?.detail || '完成失败', 'error');
    } finally {
      setItemLoading(key, false);
    }
  };

  const getSequenceName = (pathwayId: string, level: number) => {
    const pw = pathways.find(p => p.id === pathwayId);
    const seq = pw?.sequences?.find(s => s.level === level);
    return seq?.name || seq?.title || `第${level}级`;
  };

  const getSequenceColor = (level: number, pathwayColor: string) => {
    if (level <= 0) return '#FFD700';
    if (level <= 2) return '#FF6B6B';
    if (level <= 4) return '#A29BFE';
    if (level <= 6) return '#74B9FF';
    return pathwayColor;
  };

  const formatSeconds = (seconds: number) => {
    const safeSeconds = Math.max(seconds || 0, 0);
    const mm = Math.floor(safeSeconds / 60);
    const ss = safeSeconds % 60;
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  };

  const getDifficultyColor = (difficulty: string) => {
    const map: Record<string, string> = {
      '入门': '#00b894',
      '进阶': '#0984e3',
      '高级': '#e17055',
      '专家': '#d63031',
    };
    return map[difficulty] || '#6C5CE7';
  };

  const getPopularityLabel = (popularity: number) => {
    if (popularity >= 90) return '🔥🔥🔥 极热';
    if (popularity >= 75) return '🔥🔥 热门';
    if (popularity >= 60) return '🔥 较热';
    return '❄️ 小众';
  };

  const totalExp = subscriptionsData.reduce((sum, sub: any) => {
    const progress = progressMap[sub.pathway_id];
    return sum + (sub.total_exp ?? progress?.total_exp ?? 0);
  }, 0);
  const averageSequence = subscriptionsData.length
    ? (subscriptionsData.reduce((sum, sub: any) => {
        const progress = progressMap[sub.pathway_id];
        return sum + (sub.current_sequence ?? progress?.current_sequence ?? 9);
      }, 0) / subscriptionsData.length).toFixed(1)
    : '—';
  const weeklyCompleted = subscriptionsData.reduce((sum, sub: any) => sum + (sub.weekly_completed ?? 0), 0);
  const weeklyTotal = subscriptionsData.reduce((sum, sub: any) => sum + (sub.weekly_total ?? 0), 0);
  const weeklyOverallPct = weeklyTotal > 0 ? Math.round((weeklyCompleted / weeklyTotal) * 100) : 0;
  const craftScore = Math.min(100, Math.max(0, Math.round(totalExp / 2)));
  const featuredSub = subscriptionsData[0];

  const renderHomeOverview = () => (
    <section className="poa-home-orbit" style={{ backgroundImage: `linear-gradient(180deg, rgba(2,6,23,0.02), rgba(2,6,23,0.58)), url(${pathwayHomeOrbit})` }}>
      <div className="poa-home-copy">
        <span>技能修行中心</span>
        <h2>神之途径</h2>
        <p>以每日任务积累经验，在星图中推进序列，逐步形成可持续的技能修行节奏。</p>
      </div>
      <div className="poa-home-astrolabe" style={{ '--score': `${Math.max(craftScore, 3)}%` } as React.CSSProperties}>
        <strong>{craftScore}</strong>
        <span>修行势能</span>
      </div>
      <div className="poa-home-stats">
        <div><strong>{subscribedIds.size}</strong><span>已入途</span></div>
        <div><strong>{totalExp}</strong><span>总经验</span></div>
        <div><strong>{averageSequence}</strong><span>平均序列</span></div>
        <div><strong>{weeklyTotal ? `${weeklyCompleted}/${weeklyTotal}` : '—'}</strong><span>本周星轨</span></div>
      </div>
      {featuredSub && (
        <button
          className="poa-home-focus"
          style={{ '--pw-color': featuredSub.color || '#67e8f9' } as React.CSSProperties}
          onClick={() => setView({ type: 'detail', pathwayId: featuredSub.pathway_id })}
        >
          <span>今日主修</span>
          <strong>{featuredSub.name}</strong>
          <em>进入修行</em>
        </button>
      )}
    </section>
  );

  // ===== Render: My Pathways Tab =====
  const renderMyPathways = () => {
    if (subscriptionsData.length === 0) {
      return (
        <div className="poa-home">
          {renderHomeOverview()}
          <div className="poa-mine-empty">
            <div className="poa-mine-empty-icon">🌌</div>
            <h3>尚未选择途径</h3>
            <p>先选择一条星轨，系统会生成本周修行任务。</p>
            <button className="poa-action-btn primary" style={{ maxWidth: 200, margin: '0 auto' }} onClick={() => setActiveTab('all')}>
              浏览所有途径
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="poa-home">
        {renderHomeOverview()}
        <div className="poa-home-section-head">
          <div>
            <span>我的星轨</span>
            <h3>继续修行</h3>
          </div>
          <strong>{weeklyTotal ? `${weeklyOverallPct}%` : '—'}</strong>
        </div>
        <div className="poa-mine-list poa-home-rail">
          {subscriptionsData.map((sub: any, idx: number) => {
            const pw = pathways.find(p => p.id === sub.pathway_id);
            const progress = progressMap[sub.pathway_id];
            const seq = sub.current_sequence ?? progress?.current_sequence ?? 9;
            const seqPct = ((9 - seq) / 9) * 100;
            const weeklyPct = sub.weekly_total > 0 ? Math.round((sub.weekly_completed / sub.weekly_total) * 100) : 0;
            const color = sub.color || pw?.color || '#6C5CE7';
            return (
              <div
                key={sub.pathway_id}
                className="poa-mine-card poa-home-path-row"
                style={{ '--pw-color': color } as React.CSSProperties}
                onClick={() => setView({ type: 'detail', pathwayId: sub.pathway_id })}
              >
                <span
                  className={`poa-home-path-orb poa-emblem emblem-${idx % 6}`}
                  aria-hidden="true"
                />
                <div className="poa-home-path-main">
                  <div className="poa-home-path-title">
                    <strong>{sub.name}</strong>
                    <em>序列 {seq}</em>
                  </div>
                  <div className="poa-home-path-meta">
                    <span>{getSequenceName(sub.pathway_id, seq)}</span>
                    <span>{sub.total_exp ?? progress?.total_exp ?? 0} EXP</span>
                    <span>{sub.difficulty}</span>
                  </div>
                  <div className="poa-home-path-bars">
                    <i><b style={{ width: `${seqPct}%`, background: color }} /></i>
                    <i><b style={{ width: `${weeklyPct}%`, background: '#67e8f9' }} /></i>
                  </div>
                </div>
                <div className="poa-home-path-action">
                  <span>{sub.weekly_total > 0 ? `${sub.weekly_completed}/${sub.weekly_total}` : '—'}</span>
                  <b>进入</b>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ===== Render: Pathway List (所有途径 tab) =====
  const renderPathwayList = () => (
    <div className="poa-list poa-home">
      {renderHomeOverview()}
      <div className="poa-home-section-head">
        <div>
          <span>全部星域</span>
          <h3>选择新途径</h3>
        </div>
        <strong>{pathways.length}</strong>
      </div>
      <div className="poa-grid">
        {pathways.map((pw, idx) => {
          const progress = progressMap[pw.id];
          const seq = progress?.current_sequence ?? 9;
          const isSubscribed = subscribedIds.has(pw.id);
          const isSubLoading = subscribeLoading === pw.id;
          return (
            <div
              key={pw.id}
              className={`poa-card poa-home-path-card ${isSubscribed ? 'subscribed' : ''}`}
              style={{ '--pw-color': pw.color } as React.CSSProperties}
              onClick={() => setView({ type: 'detail', pathwayId: pw.id })}
            >
              <div className="poa-card-header">
                <span className={`poa-card-icon poa-emblem emblem-${idx % 6}`} aria-hidden="true" />
                <div className="poa-card-seq" style={{ background: getSequenceColor(seq, pw.color) }}>
                  第{seq}级
                </div>
              </div>
              <h3 className="poa-card-name">{pw.name}</h3>
              <p className="poa-card-desc">{pw.description}</p>

              {/* 新字段：热度 / 难度 / 信徒数量 */}
              <div className="poa-card-meta-row">
                <span className="poa-meta-tag poa-meta-popularity">
                  {getPopularityLabel(pw.popularity ?? 50)}
                </span>
                <span
                  className="poa-meta-tag poa-meta-difficulty"
                  style={{ background: `${getDifficultyColor(pw.difficulty)}22`, color: getDifficultyColor(pw.difficulty), borderColor: `${getDifficultyColor(pw.difficulty)}44` }}
                >
                  ⚔️ {pw.difficulty}
                </span>
                <span className="poa-meta-tag poa-meta-followers">
                  👥 {pw.follower_count ?? 0} 信徒
                </span>
              </div>

              <div className="poa-card-footer">
                <span className="poa-card-rank">{getSequenceName(pw.id, seq)}</span>
                <span className="poa-card-exp">⚡ {progress?.total_exp ?? 0} EXP</span>
              </div>
              <div className="poa-card-bar">
                <div className="poa-card-bar-fill" style={{ width: `${((9 - seq) / 9) * 100}%`, background: pw.color }} />
              </div>
              {/* 热度进度条 */}
              <div className="poa-card-popularity-bar">
                <div className="poa-card-popularity-fill" style={{ width: `${pw.popularity ?? 50}%`, background: `linear-gradient(90deg, ${pw.color}99, ${pw.color})` }} />
              </div>

              {/* 订阅按钮 */}
              <button
                className={`poa-subscribe-btn ${isSubscribed ? 'subscribed' : ''}`}
                disabled={isSubLoading}
                onClick={(e) => toggleSubscribe(pw.id, e)}
              >
                {isSubLoading ? '...' : isSubscribed ? '已入途' : '选择途径'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ===== Render: Pathway Detail =====
  const renderPathwayDetail = (pathwayId: string) => {
    const pw = pathways.find(p => p.id === pathwayId);
    if (!pw) return null;
    const progress = progressMap[pathwayId];
    const currentSeq = progress?.current_sequence ?? 9;
    const canUpgrade = !!progress?.can_upgrade;
    const nextLevel = progress?.next_level ?? (currentSeq > 0 ? currentSeq - 1 : null);
    const currentExp = progress?.total_exp ?? 0;
    const nextLevelExpRequired = progress?.next_level_exp_required ?? null;
    const progressPercent = progress?.progress_percent ?? 0;
    const remaining = Math.max((nextLevelExpRequired ?? 0) - currentExp, 0);
    const todayItems = dailyTasks && dailyTasks.pathway_id === pathwayId ? dailyTasks.items || [] : [];
    const todayDone = todayItems.filter(t => t.status === 'completed').length;
    const todayTotal = todayItems.length;
    const todayExp = todayItems.filter(t => t.status === 'completed').reduce((sum, t) => sum + (t.exp || 0), 0);
    const todayCap = todayItems.reduce((sum, t) => sum + (t.exp || 0), 0);
    const weekItems = weeklyTasks && weeklyTasks.pathway_id === pathwayId ? weeklyTasks.items || [] : [];
    const weekDone = weekItems.filter((t: any) => t.status === 'completed').length;
    const weekTotal = weekItems.length;
    const weekPct = weekTotal > 0 ? Math.round((weekDone / weekTotal) * 100) : 0;
    const ascendingSeqs = [...(pw.sequences || [])].sort((a, b) => b.level - a.level); // 9 → 0
    const currentInfo = pw.sequences.find(seq => seq.level === currentSeq);
    const dimensionProfile = (pw.dimension_profile || []).filter(d => (d.weight ?? 0) > 0);
    // 当前序列的考核方式（优先取进度返回值，回退到序列定义）
    const currentVtype: VerificationType =
      (progress?.verification_type as VerificationType) ||
      (currentInfo?.verification_type as VerificationType) ||
      'quiz';
    const vmeta = VERIFICATION_META[currentVtype] || VERIFICATION_META.quiz;

    return (
      <div className="poa-detail poa-star-detail">
        <section className="poa-star-atlas" style={{ backgroundImage: `linear-gradient(180deg, rgba(2,6,23,0.04), rgba(2,6,23,0.78)), url(${pathwayConstellationAtlas})` }}>
          <div className="poa-star-topline">
            <div className="poa-star-topline-main">
              <div className="poa-star-kicker">神之途径 · Path of Ascension</div>
              <h2>{pw.name}</h2>
              <div className="poa-star-chips">
                <span
                  className="poa-star-chip"
                  style={{ color: getDifficultyColor(pw.difficulty), borderColor: `${getDifficultyColor(pw.difficulty)}66`, background: `${getDifficultyColor(pw.difficulty)}1f` }}
                >
                  ⚔️ {pw.difficulty}
                </span>
                <span className="poa-star-chip popularity">{getPopularityLabel(pw.popularity ?? 50)}</span>
                <span className="poa-star-chip followers">👥 {pw.follower_count ?? 0} 信徒</span>
              </div>
            </div>
            <button
              className={`poa-star-subscribe ${subscribedIds.has(pathwayId) ? 'subscribed' : ''}`}
              disabled={subscribeLoading === pathwayId}
              onClick={(e) => toggleSubscribe(pathwayId, e)}
            >
              {subscribedIds.has(pathwayId) ? '✓ 已入途' : '入途'}
            </button>
          </div>

          {pw.description && (
            <p className="poa-star-intro">{pw.description}</p>
          )}

          <div className="poa-star-identity">
            <span className="poa-star-orb" style={{ background: `radial-gradient(circle, ${getSequenceColor(currentSeq, pw.color)}f2, rgba(30,27,75,0.72))` }}>{currentSeq}</span>
            <div className="poa-star-identity-copy">
              <span>当前序列</span>
              <strong>{currentInfo?.name || getSequenceName(pathwayId, currentSeq)}</strong>
              <p>{currentInfo?.description || pw.description}</p>
            </div>
            <div className="poa-star-exp">
              <div><strong>{currentExp}</strong><span> / {nextLevelExpRequired ?? 'MAX'} EXP</span></div>
              <div className="poa-star-exp-bar" aria-label={`晋升进度 ${Math.round(progressPercent)}%`}>
                <i style={{ width: `${Math.max(progressPercent, 3)}%`, background: pw.color }} />
              </div>
              <span>{nextLevel !== null ? `达至序列 ${nextLevel} 还需 ${remaining} EXP` : '已抵达此途径顶点'}</span>
            </div>
          </div>

          <div className="poa-star-map poa-sequence-route" aria-label={`${pw.name}序列星轨`}>
            <div className="poa-star-map-heading">
              <span>序列星轨</span>
              <strong>从序列 9 起步，逐阶抵达序列 0</strong>
            </div>
            <div className="poa-sequence-scroll">
              <div className="poa-sequence-track">
            {ascendingSeqs.map((seq) => {
              const isCompleted = seq.level > currentSeq;
              const isCurrent = seq.level === currentSeq;
              const isLocked = seq.level < currentSeq;
              return (
                <div key={seq.level} className={`poa-sequence-step ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${isLocked ? 'locked' : ''}`}>
                  <button
                    className="poa-star-node"
                    style={{ '--pw-color': pw.color } as React.CSSProperties}
                    onClick={() => isCurrent && currentSeq > 0 && canUpgrade ? startVerification(pathwayId, currentSeq, currentVtype) : undefined}
                    disabled={!isCurrent || !canUpgrade || currentSeq <= 0}
                    aria-label={`序列 ${seq.level}，${seq.name}，${isCompleted ? '已完成' : isCurrent ? '当前序列' : '未解锁'}`}
                    title={`${seq.title} · ${seq.name}`}
                  ><span>{seq.level}</span></button>
                  <strong title={seq.name}>{seq.name}</strong>
                </div>
              );
            })}
              </div>
            </div>
            <div className="poa-star-next-card">
              <small>下一目标</small>
              <strong>{nextLevel !== null ? `序列 ${nextLevel} · ${getSequenceName(pathwayId, nextLevel)}` : '至高无上者'}</strong>
              <span>{nextLevel !== null ? `还需 ${remaining} EXP` : '已完成全部晋升'}</span>
            </div>
          </div>
        </section>

        <section className="poa-star-status">
          <div className="poa-star-week">
            <span>本周星轨</span>
            <strong>{weekDone}/{weekTotal || '—'}</strong>
            <div className="poa-star-mini-bar"><i style={{ width: `${weekPct}%`, background: pw.color }} /></div>
            <small>{weekTotal ? `${weekPct}% · ${weeklyTasks?.week_start || ''}` : '入途后生成周任务'}</small>
          </div>
          <button
            className={`poa-star-exam ${canUpgrade ? 'ready' : ''}`}
            disabled={!canUpgrade || currentSeq <= 0}
            onClick={() => startVerification(pathwayId, currentSeq, currentVtype)}
          >
            <span>{vmeta.icon} {currentSeq <= 0 ? '晋升考核' : vmeta.label}</span>
            <strong>{currentSeq <= 0 ? '已满级' : canUpgrade ? '可发起' : '准备中'}</strong>
            <small>{currentSeq <= 0 ? '已抵达顶点' : canUpgrade ? vmeta.verb : `还差 ${remaining} EXP`}</small>
          </button>
        </section>

        {/* 维度画像：展示该途径对六维成长的贡献 */}
        {dimensionProfile.length > 0 && (
          <section className="poa-star-dimension">
            <div className="poa-star-dimension-head">
              <div>
                <h3>修行维度</h3>
                <span>完成本途径任务将主要提升以下维度</span>
              </div>
              {pw.primary_dimension && (
                <span className="poa-star-dimension-primary" style={{ color: pw.primary_dimension.color }}>
                  {pw.primary_dimension.icon} 主修 · {pw.primary_dimension.name}
                </span>
              )}
            </div>
            <div className="poa-star-dimension-list">
              {dimensionProfile.map((dim) => (
                <div key={dim.key} className="poa-star-dimension-row">
                  <span className="poa-star-dimension-label">
                    <b style={{ color: dim.color }}>{dim.icon}</b>
                    {dim.name}
                  </span>
                  <div className="poa-star-dimension-bar">
                    <i style={{ width: `${dim.weight}%`, background: `linear-gradient(90deg, ${dim.color}aa, ${dim.color})` }} />
                  </div>
                  <em>{dim.weight}%</em>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="poa-star-tasks">
          <div className="poa-star-tasks-head">
            <div>
              <h3>今日星轨</h3>
              <span>完成学习任务，积累星辰之力</span>
            </div>
            <strong>{todayExp}/{todayCap || 0} EXP</strong>
          </div>
          {dailyTasksLoading ? (
            <div className="poa-daily-loading">加载中...</div>
          ) : todayItems.length ? (
            <div className="poa-star-task-list">
              {todayItems.map((task) => (
                <div key={task.id} className={`poa-star-task ${task.status === 'completed' ? 'done' : ''}`}>
                  <div className="poa-star-task-mark"><span>{task.status === 'completed' ? '✓' : task.category?.slice(0, 1) || '修'}</span></div>
                  <div className="poa-star-task-body">
                    <div className="poa-star-task-title">{task.title}</div>
                    {task.description && <p>{task.description}</p>}
                    <div className="poa-star-task-meta">
                      {task.category && <span>{task.category}</span>}
                      <b>+{task.exp} EXP</b>
                    </div>
                  </div>
                  {task.status !== 'completed' && (
                    <button
                      className="poa-star-task-action"
                      disabled={task.added_to_daily || isItemLoading(`add_daily_${task.id}`)}
                      onClick={() => addDailyTask(pathwayId, task.id)}
                    >{task.added_to_daily ? '已加入今日' : '加入今日'}</button>
                  )}
                  {task.status !== 'completed' ? (
                    <button
                      className="poa-star-task-action"
                      disabled={isItemLoading(`daily_${task.id}`)}
                      onClick={() => completeDailyTask(pathwayId, task.id)}
                    >
                      {isItemLoading(`daily_${task.id}`) ? '...' : '标记完成'}
                    </button>
                  ) : (
                    <span className="poa-star-task-done">已完成</span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="poa-daily-empty">
              <p>暂无今日任务</p>
              <button className="poa-action-btn primary" onClick={() => loadTodayDailyTasks(pathwayId)}>生成今日星轨</button>
            </div>
          )}
          <div className="poa-star-task-foot">今日任务 {todayDone}/{todayTotal || 0} · 全部完成可额外稳固修行节奏</div>
        </section>

        {subscribedIds.has(pathwayId) && weekItems.length > 0 && (
          <section className="poa-star-week-list">
            <h3>本周修行</h3>
            {weeklyTasksLoading && <div className="poa-daily-loading">加载中...</div>}
            {weekItems.slice(0, 4).map((task: any) => (
              <div key={task.id} className={`poa-star-week-row ${task.status === 'completed' ? 'done' : ''}`}>
                <span>{task.status === 'completed' ? '✓' : '○'}</span>
                <b>{task.title}</b>
                <em>+{task.exp}</em>
                {task.status !== 'completed' && (
                  <button disabled={isItemLoading(`weekly_${task.id}`)} onClick={() => completeWeeklyTask(pathwayId, task.id)}>
                    {isItemLoading(`weekly_${task.id}`) ? '...' : '完成'}
                  </button>
                )}
              </div>
            ))}
          </section>
        )}
      </div>
    );
  };

  // ===== Render: Quiz =====
  const renderQuiz = (pathwayId: string) => {
    if (!quizData) return null;
    const pw = pathways.find(p => p.id === pathwayId);
    const allAnswered = quizData.questions.every(q => answers[String(q.id)] !== undefined);
    const isTimedOut = quizRemainingSeconds <= 0;

    return (
      <div className="poa-quiz">
        <div className="poa-quiz-header" style={{ background: pw?.color || '#6C5CE7' }}>
          <h2>{quizData.sequence_name} · 第{quizData.sequence_level}级升级考核</h2>
          <p>限时 {formatSeconds(quizData.time_limit_seconds)}，得分需达到 {quizData.pass_percent}%</p>
          <div className={`poa-quiz-timer ${isTimedOut ? 'danger' : quizRemainingSeconds <= 60 ? 'warn' : ''}`}>
            剩余时间：{formatSeconds(quizRemainingSeconds)}
          </div>
        </div>
        <div className="poa-quiz-questions">
          {quizData.questions.map((q, idx) => (
            <div key={q.id} className="poa-question">
              <div className="poa-question-num">第 {idx + 1} 题</div>
              <h4 className="poa-question-text">{q.question}</h4>
              <div className="poa-options">
                {q.options.map((opt, optIdx) => (
                  <button
                    key={optIdx}
                    className={`poa-option ${answers[String(q.id)] === optIdx ? 'selected' : ''}`}
                    onClick={() => setAnswers(prev => ({ ...prev, [String(q.id)]: optIdx }))}
                  >
                    <span className="poa-option-letter">{String.fromCharCode(65 + optIdx)}</span>
                    <span className="poa-option-text">{opt}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <button
          className="poa-submit-btn"
          disabled={!allAnswered || isItemLoading('quiz_submit') || isTimedOut}
          onClick={() => submitQuiz()}
        >
          {isItemLoading('quiz_submit') ? '提交中...' : isTimedOut ? '已超时' : '提交答案'}
        </button>
      </div>
    );
  };

  const renderVerification = (pathwayId: string, level: number) => {
    const pw = pathways.find(p => p.id === pathwayId);
    const currentInfo = pw?.sequences?.find(sequence => sequence.level === level);
    const vtype = (verificationInfo?.verification_type || currentInfo?.verification_type || 'practice') as VerificationType;
    const meta = VERIFICATION_META[vtype] || VERIFICATION_META.practice;
    const required = verificationInfo?.required_days || currentInfo?.verification_config?.required_days || 1;
    const completed = verificationInfo?.progress_count ?? verificationInfo?.completed_days ?? 0;
    const minWords = currentInfo?.verification_config?.min_words || verificationInfo?.verification_config?.min_words || 100;

    return (
      <div className="poa-verification">
        <section className="poa-verification-hero" style={{ '--pw-color': pw?.color || '#8b5cf6' } as React.CSSProperties}>
          <span>{meta.icon}</span>
          <div><small>序列 {level} 晋升验证</small><h2>{meta.label}</h2><p>{meta.desc}</p></div>
        </section>
        <section className="poa-verification-card">
          {(vtype === 'practice' || vtype === 'streak') && (
            <><div className="poa-verification-progress"><strong>{completed}</strong><span>/ {required} 天</span></div><div className="poa-verification-bar"><i style={{ width: `${Math.min(100, (completed / required) * 100)}%`, background: pw?.color }} /></div><p>完成规定修行天数后即可确认晋升。</p></>
          )}
          {vtype === 'reflection' && (
            <label className="poa-verification-field"><span>修行反思 · 至少 {minWords} 字</span><textarea value={reflectionText} onChange={event => setReflectionText(event.target.value)} placeholder={currentInfo?.verification_config?.prompt || '记录你的实践、收获与下一步计划'} rows={7} /><small>{reflectionText.trim().length} / {minWords}</small></label>
          )}
          {vtype === 'milestone' && (
            <label className="poa-verification-field"><span>成果凭证（可选）</span><input value={evidenceUrl} onChange={event => setEvidenceUrl(event.target.value)} placeholder="粘贴作品、项目或成果链接" /><small>{currentInfo?.verification_config?.prompt || '确认你已经完成本序列要求的真实成果。'}</small></label>
          )}
          <button
            className="poa-submit-btn"
            disabled={isItemLoading('verify_submit') || (vtype === 'reflection' && reflectionText.trim().length < minWords) || ((vtype === 'practice' || vtype === 'streak') && completed < required)}
            onClick={() => submitVerification(pathwayId, level, vtype)}
          >{isItemLoading('verify_submit') ? '提交中...' : meta.verb}</button>
        </section>
      </div>
    );
  };

  // ===== Render: Quiz Result =====
  const renderQuizResult = (pathwayId: string, result: QuizResult) => {
    return (
      <div className="poa-result">
        <div className={`poa-result-card ${result.passed ? 'passed' : 'failed'}`}>
          <div className="poa-result-icon">{result.passed ? '🎉' : '😔'}</div>
          <h2>{result.passed ? '考核通过！' : '未能通过'}</h2>
          <p className="poa-result-score">
            答对 <strong>{result.score}</strong> / {result.total_questions} 题，得分 {result.score_percent}%
          </p>
          <div className="poa-result-exp" style={{ opacity: result.exp_gained ? 1 : 0.75 }}>
            用时 {formatSeconds(result.time_spent_seconds)} / {formatSeconds(result.time_limit_seconds)}
          </div>
          {result.passed && result.new_sequence != null && (
            <div className="poa-result-upgrade">
              🔥 晋升至 <strong>第{result.new_sequence}级 · {getSequenceName(pathwayId, result.new_sequence)}</strong>
            </div>
          )}
          {!result.passed && (
            <p className="poa-result-hint">
              {!result.within_time_limit ? '本次答题超时，请重新开始升级考核。' : '得分未达标，继续积累后再次挑战吧。'}
            </p>
          )}
          <div className="poa-result-actions">
            <button className="poa-action-btn primary" onClick={() => setView({ type: 'detail', pathwayId })}>
              返回途径
            </button>
            {!result.passed && (
              <button className="poa-action-btn secondary" onClick={() => loadQuiz(pathwayId, quizData?.sequence_level || 9)}>
                再次挑战
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ===== Render: Level-Up Animation =====
  const renderLevelUp = (pathwayId: string, result: QuizResult) => {
    const pw = pathways.find(p => p.id === pathwayId);
    const newLevel = result.new_sequence ?? 0;
    const oldLevel = newLevel + 1;
    const newSeq = pw?.sequences.find(s => s.level === newLevel);
    const pwColor = pw?.color || '#6C5CE7';

    const particles = Array.from({ length: 30 }, (_, i) => {
      const angle = (i / 30) * 360;
      const dist = 80 + Math.random() * 120;
      const size = 3 + Math.random() * 5;
      const dur = 0.8 + Math.random() * 0.6;
      const delay = Math.random() * 0.3;
      return (
        <div
          key={i}
          className="lu-particle"
          style={{
            '--angle': `${angle}deg`,
            '--dist': `${dist}px`,
            '--size': `${size}px`,
            '--dur': `${dur}s`,
            '--delay': `${delay}s`,
            '--hue': `${(i * 12) % 360}`,
          } as React.CSSProperties}
        />
      );
    });

    return (
      <div className="lu-overlay" onClick={() => setView({ type: 'quiz-result', pathwayId, result })}>
        <div className="lu-scene" onClick={e => e.stopPropagation()}>
          {/* Particles */}
          <div className="lu-particles">{particles}</div>

          {/* Glow rings */}
          <div className="lu-ring lu-ring-1" style={{ borderColor: pwColor }} />
          <div className="lu-ring lu-ring-2" style={{ borderColor: pwColor }} />

          {/* Icon */}
          <div className="lu-icon">{pw?.icon || '⚡'}</div>

          {/* Level transition */}
          <div className="lu-level-wrap">
            <span className="lu-level-old">Lv.{oldLevel}</span>
            <span className="lu-arrow">→</span>
            <span className="lu-level-new" style={{ color: pwColor }}>Lv.{newLevel}</span>
          </div>

          <h2 className="lu-title">晋升成功</h2>
          <div className="lu-badge" style={{ background: `linear-gradient(135deg, ${pwColor}, ${pwColor}88)` }}>
            第{newLevel}级 · {getSequenceName(pathwayId, newLevel)}
          </div>

          {/* Next stage info */}
          {newSeq && (
            <div className="lu-next-stage">
              <div className="lu-next-label">接下来你将学习</div>
              <div className="lu-next-name" style={{ color: pwColor }}>{newSeq.name}</div>
              <div className="lu-next-desc">{newSeq.description}</div>
            </div>
          )}

          <button className="lu-continue-btn" style={{ background: pwColor }} onClick={() => setView({ type: 'quiz-result', pathwayId, result })}>
            查看详情
          </button>
        </div>
      </div>
    );
  };

  // 根据当前视图决定返回行为：list视图返回App，其他视图返回上一层
  const handleHeaderBack = () => {
    if (view.type === 'list') {
      onClose();
    } else if (view.type === 'detail') {
      setView({ type: 'list' });
    } else if (view.type === 'quiz' || view.type === 'verify') {
      setView({ type: 'detail', pathwayId: view.pathwayId });
    } else if (view.type === 'quiz-result') {
      setView({ type: 'detail', pathwayId: view.pathwayId });
    } else if (view.type === 'level-up') {
      setView({ type: 'detail', pathwayId: view.pathwayId });
    }
  };

  useAppBackHandler(() => {
    if (view.type === 'list') return false;
    handleHeaderBack();
    return true;
  });


  const isInSubView = view.type !== 'list';
  const activePathway = isInSubView && 'pathwayId' in view
    ? pathways.find(pathway => pathway.id === view.pathwayId)
    : null;
  const subviewLabel = view.type === 'quiz'
    ? `晋升考核 · 序列 ${view.level}`
    : view.type === 'verify'
      ? `晋升验证 · 序列 ${view.level}`
      : view.type === 'quiz-result' || view.type === 'level-up'
        ? '晋升结果'
        : '修行中心';

  return (
    <div className="poa-container">
      {/* Toast 通知 */}
      {toast && (
        <div className={`poa-toast ${toast.type}`}>
          {toast.type === 'success' ? '✅' : '❌'} {toast.msg}
        </div>
      )}

      {/* Header with user info + 3 progress bars */}
      <header className="poa-header">
        <div className="poa-header-bg" />
        <div className="poa-header-inner">
          <button className="poa-close" onClick={handleHeaderBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          <div className="poa-header-heading">
            <h1>{activePathway?.name || '神之途径'}</h1>
            <span>{isInSubView ? subviewLabel : 'Path of Ascension'}</span>
          </div>
          <button className="poa-close" aria-label={isInSubView ? '返回途径中心' : '通知'} onClick={() => isInSubView ? setView({ type: 'list' }) : undefined}>
            {isInSubView ? <span className="poa-header-sigil">✦</span> : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>}
          </button>
        </div>

      </header>

      <button className="poa-tab" onClick={() => navigation.navigate("records", {type: "study"})}>学习日志 · 查看与编辑</button>
      {/* Tab Bar（仅列表视图显示） */}
      {!isInSubView && (
        <div className="poa-tab-bar">
          <button
            className={`poa-tab ${activeTab === 'mine' ? 'active' : ''}`}
            onClick={() => setActiveTab('mine')}
          >
            我的途径
            {subscribedIds.size > 0 && <span className="poa-tab-badge">{subscribedIds.size}</span>}
          </button>
          <button
            className={`poa-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            所有途径
          </button>
        </div>
      )}

      {/* Content */}
      <main className="poa-main">
        {isInSubView ? (
          <>
            {view.type === 'detail' && renderPathwayDetail(view.pathwayId)}
            {view.type === 'quiz' && renderQuiz(view.pathwayId)}
            {view.type === 'verify' && renderVerification(view.pathwayId, view.level)}
            {view.type === 'quiz-result' && renderQuizResult(view.pathwayId, view.result)}
            {view.type === 'level-up' && renderLevelUp(view.pathwayId, view.result)}
          </>
        ) : loading && !pathways.length ? (
          <div className="poa-loading"><div className="poa-spinner" /><p>加载中...</p></div>
        ) : activeTab === 'mine' ? renderMyPathways() : renderPathwayList()
        }
      </main>

      <style>{`
        .poa-container {
          min-height: 100vh;
          background:
            radial-gradient(circle at 50% -8%, rgba(124,58,237,0.34), transparent 34%),
            radial-gradient(circle at 14% 20%, rgba(34,211,238,0.14), transparent 28%),
            linear-gradient(180deg, #050816 0%, #090a1f 46%, #020617 100%);
          padding-bottom: 40px;
          color: #e5e7eb;
          overflow-x: hidden;
        }
        .poa-container::before {
          content: '';
          position: fixed;
          inset: 0;
          pointer-events: none;
          background-image:
            radial-gradient(circle, rgba(255,255,255,0.55) 0 1px, transparent 1.5px),
            radial-gradient(circle, rgba(103,232,249,0.35) 0 1px, transparent 1.5px);
          background-size: 58px 58px, 91px 91px;
          background-position: 6px 12px, 28px 35px;
          opacity: 0.16;
          z-index: 0;
        }
        .poa-container > * { position: relative; z-index: 1; }

        /* Toast 通知 */
        .poa-toast {
          position: fixed; top: calc(20px + var(--status-bar-height, 28px));
          left: 50%; transform: translateX(-50%);
          z-index: 9999;
          padding: 12px 20px; border-radius: 12px;
          font-size: 14px; font-weight: 600;
          white-space: nowrap; max-width: 90vw;
          box-shadow: 0 4px 20px rgba(0,0,0,0.4);
          animation: poaToastIn 0.3s ease;
        }
        .poa-toast.success {
          background: rgba(0,184,148,0.95); color: white;
        }
        .poa-toast.error {
          background: rgba(225,112,85,0.95); color: white;
        }
        @keyframes poaToastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }

        /* Header */
        .poa-header {
          position: sticky;
          top: 0;
          z-index: 100;
          overflow: hidden;
          backdrop-filter: blur(18px);
        }
        .poa-header-bg {
          position: absolute; inset: 0;
          background:
            radial-gradient(circle at 50% 0%, rgba(232,215,167,0.18), transparent 34%),
            linear-gradient(180deg, rgba(7,10,28,0.96) 0%, rgba(13,18,45,0.84) 72%, rgba(13,18,45,0) 100%);
        }
        .poa-header-bg::after {
          content: '';
          position: absolute;
          left: 24px;
          right: 24px;
          bottom: 8px;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(103,232,249,0.42), transparent);
        }
        .poa-header-inner {
          position: relative; z-index: 1;
          display: grid;
          grid-template-columns: 42px 1fr 42px;
          align-items: center;
          gap: 10px;
          padding: 14px 16px 18px;
          padding-top: calc(14px + var(--status-bar-height, 28px));
        }
        .poa-header-heading {
          min-width: 0;
          text-align: center;
        }
        .poa-header-heading h1 {
          color: #fff7db;
          font-size: 20px;
          font-weight: 900;
          letter-spacing: 0.18em;
          margin: 0 0 3px;
          text-indent: 0.18em;
          text-shadow: 0 0 22px rgba(232,215,167,0.28);
        }
        .poa-header-heading span {
          display: block;
          color: rgba(148,163,184,0.86);
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.28em;
          text-transform: uppercase;
        }
        .poa-close {
          width: 40px; height: 40px; border-radius: 50%;
          background: rgba(15,23,42,0.54); border: 1px solid rgba(148,163,184,0.22);
          color: #e5e7eb; display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.2s;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 22px rgba(0,0,0,0.2);
        }
        .poa-close:active { transform: scale(0.9); background: rgba(30,41,59,0.72); }

        /* Tab Bar */
        .poa-tab-bar {
          display: flex; gap: 4px;
          margin: 2px 16px 0;
          background: rgba(15,23,42,0.66);
          border-radius: 999px;
          padding: 5px;
          border: 1px solid rgba(148,163,184,0.18);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 14px 34px rgba(0,0,0,0.24);
          backdrop-filter: blur(14px);
        }
        .poa-tab {
          flex: 1; padding: 11px 8px;
          border: none; border-radius: 999px;
          background: transparent;
          color: rgba(203,213,225,0.68);
          font-size: 14px; font-weight: 800;
          cursor: pointer; font-family: inherit;
          transition: all 200ms;
          display: flex; align-items: center; justify-content: center; gap: 6px;
        }
        .poa-tab.active {
          background: linear-gradient(135deg, rgba(103,232,249,0.22), rgba(167,139,250,0.26));
          color: #f8fafc;
          box-shadow: 0 0 22px rgba(103,232,249,0.18), inset 0 1px 0 rgba(255,255,255,0.12);
        }
        .poa-tab:active { transform: scale(0.97); }
        .poa-tab-badge {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 18px; height: 18px; padding: 0 5px;
          border-radius: 9px; background: #6C5CE7; color: white;
          font-size: 11px; font-weight: 800;
        }

        /* Main */
        .poa-main { padding: 0 16px; margin-top: 12px; }

        /* Loading */
        .poa-loading {
          display: flex; flex-direction: column; align-items: center;
          padding: 80px 20px; gap: 16px;
        }
        .poa-spinner {
          width: 36px; height: 36px; border: 3px solid #ddd;
          border-top-color: #6C5CE7; border-radius: 50%;
          animation: poaSpin 0.8s linear infinite;
        }
        @keyframes poaSpin { to { transform: rotate(360deg); } }

        /* Hero */
        .poa-hero {
          text-align: center; padding: 20px 0 28px;
        }
        .poa-hero-icon { font-size: 48px; margin-bottom: 8px; }
        .poa-hero h2 { margin: 0 0 8px; font-size: 24px; color: var(--text-primary, #E2E8F0); }
        .poa-hero p { margin: 0; color: var(--text-secondary, #94A3B8); font-size: 14px; }

        /* Grid */
        .poa-grid {
          display: grid; grid-template-columns: 1fr; gap: 16px;
        }
        @media (min-width: 600px) {
          .poa-grid { grid-template-columns: 1fr 1fr; }
        }

        /* Card */
        .poa-card {
          position: relative;
          overflow: hidden;
          background: linear-gradient(180deg, rgba(30,41,59,0.78), rgba(15,23,42,0.88));
          border-radius: 22px;
          padding: 20px; cursor: pointer;
          box-shadow: 0 18px 44px rgba(0,0,0,0.28);
          transition: all 0.2s; border: 1px solid rgba(148,163,184,0.16);
          backdrop-filter: blur(16px);
        }
        .poa-card::before {
          content: '';
          position: absolute;
          inset: -1px;
          background: radial-gradient(circle at 18% 0%, var(--pw-color, #6C5CE7)33, transparent 34%);
          opacity: 0.55;
          pointer-events: none;
        }
        .poa-card > * { position: relative; z-index: 1; }
        .poa-card:active { transform: scale(0.98); }
        .poa-card-header {
          display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;
        }
        .poa-card-icon { font-size: 36px; }
        .poa-card-seq {
          padding: 4px 12px; border-radius: 20px; color: white;
          font-size: 12px; font-weight: 700;
        }
        .poa-card-name {
          margin: 0 0 8px; font-size: 16px; font-weight: 700;
          color: var(--text-primary, #E2E8F0);
        }
        .poa-card-desc {
          margin: 0 0 16px; font-size: 13px; color: var(--text-secondary, #94A3B8);
          line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 2;
          -webkit-box-orient: vertical; overflow: hidden;
        }
        .poa-card-footer {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 10px;
        }
        .poa-card-rank { font-size: 13px; font-weight: 600; color: var(--text-primary, #E2E8F0); }
        .poa-card-exp { font-size: 12px; color: var(--text-secondary, #94A3B8); }
        .poa-card-bar {
          height: 4px; background: var(--border-light, rgba(51,65,85,0.5)); border-radius: 2px; overflow: hidden;
          margin-bottom: 6px;
        }
        .poa-card-bar-fill {
          height: 100%; border-radius: 2px; transition: width 0.5s ease;
        }

        /* Meta row */
        .poa-card-meta-row {
          display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px;
        }
        .poa-meta-tag {
          display: inline-flex; align-items: center;
          padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600;
          border: 1px solid transparent;
        }
        .poa-meta-popularity {
          background: rgba(255, 107, 107, 0.12);
          color: #ff6b6b;
          border-color: rgba(255, 107, 107, 0.25);
        }
        .poa-meta-followers {
          background: rgba(108, 92, 231, 0.12);
          color: #a29bfe;
          border-color: rgba(108, 92, 231, 0.25);
        }

        /* Popularity bar */
        .poa-card-popularity-bar {
          height: 3px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; margin-top: 2px;
        }
        .poa-card-popularity-fill {
          height: 100%; border-radius: 2px; transition: width 0.6s ease;
        }

        /* Subscribe button on card */
        .poa-subscribe-btn {
          display: block; width: 100%; margin-top: 12px;
          padding: 9px 0; border-radius: 10px; border: 1.5px solid rgba(108,92,231,0.4);
          background: rgba(108,92,231,0.08); color: #a29bfe;
          font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit;
          transition: all 0.2s;
        }
        .poa-subscribe-btn:active { transform: scale(0.97); }
        .poa-subscribe-btn.subscribed {
          background: rgba(0,184,148,0.12); color: #00b894;
          border-color: rgba(0,184,148,0.3);
        }
        .poa-subscribe-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Subscribed card border */
        .poa-card.subscribed {
          border-color: rgba(0,184,148,0.35);
          box-shadow: 0 2px 16px rgba(0,184,148,0.12);
        }

        /* Hero sub count */
        .poa-hero-sub-count {
          display: inline-block; margin-top: 8px;
          padding: 4px 14px; border-radius: 20px;
          background: rgba(0,184,148,0.15); color: #00b894;
          font-size: 12px; font-weight: 700;
          border: 1px solid rgba(0,184,148,0.25);
        }

        /* ===== 我的途径 Tab ===== */
        .poa-mine-empty {
          display: flex; flex-direction: column; align-items: center;
          padding: 60px 20px; gap: 12px; text-align: center;
        }
        .poa-mine-empty-icon { font-size: 56px; margin-bottom: 4px; }
        .poa-mine-empty h3 { margin: 0; font-size: 18px; font-weight: 700; color: var(--text-primary, #E2E8F0); }
        .poa-mine-empty p { margin: 0; font-size: 13px; color: var(--text-secondary, #94A3B8); }

        .poa-mine-list { display: flex; flex-direction: column; gap: 14px; padding-top: 4px; }

        .poa-mine-card {
          position: relative;
          overflow: hidden;
          background: linear-gradient(180deg, rgba(30,41,59,0.76), rgba(15,23,42,0.9));
          border-radius: 22px;
          padding: 18px; cursor: pointer;
          box-shadow: 0 18px 44px rgba(0,0,0,0.28);
          border: 1px solid rgba(148,163,184,0.16);
          border-left: 4px solid var(--pw-color, #6C5CE7);
          transition: all 0.2s;
          backdrop-filter: blur(16px);
        }
        .poa-mine-card::before {
          content: '';
          position: absolute;
          inset: -1px;
          background: radial-gradient(circle at 14% 0%, var(--pw-color, #6C5CE7)2f, transparent 36%);
          pointer-events: none;
        }
        .poa-mine-card > * { position: relative; z-index: 1; }
        .poa-mine-card:active { transform: scale(0.98); }

        .poa-mine-card-top {
          display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px;
        }
        .poa-mine-icon { font-size: 32px; flex-shrink: 0; margin-top: 2px; }
        .poa-mine-card-info { flex: 1; min-width: 0; }
        .poa-mine-name {
          font-size: 15px; font-weight: 700; color: var(--text-primary, #E2E8F0);
          margin-bottom: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .poa-mine-tags { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
        .poa-mine-seq-badge {
          padding: 2px 10px; border-radius: 20px; color: white;
          font-size: 11px; font-weight: 700;
        }
        .poa-mine-rank { font-size: 11px; color: var(--text-secondary, #94A3B8); font-weight: 600; }
        .poa-mine-exp {
          font-size: 13px; font-weight: 700; color: #ffd700;
          flex-shrink: 0; white-space: nowrap;
        }

        .poa-mine-bar-row {
          display: flex; align-items: center; gap: 10px; margin-bottom: 8px;
        }
        .poa-mine-bar-label {
          font-size: 11px; color: var(--text-tertiary, #64748B); min-width: 24px;
        }
        .poa-mine-bar-track {
          flex: 1; height: 6px; background: rgba(255,255,255,0.06); border-radius: 3px; overflow: hidden;
        }
        .poa-mine-bar-fill {
          height: 100%; border-radius: 3px; transition: width 0.5s ease;
        }
        .poa-mine-bar-pct {
          font-size: 11px; font-weight: 700; color: var(--text-secondary, #94A3B8); min-width: 32px; text-align: right;
        }

        .poa-mine-card-footer {
          display: flex; justify-content: space-between; align-items: center;
          margin-top: 10px; padding-top: 10px;
          border-top: 1px solid var(--border-light, rgba(51,65,85,0.4));
        }
        .poa-mine-popularity { font-size: 12px; color: var(--text-secondary, #94A3B8); }
        .poa-mine-enter { font-size: 13px; font-weight: 700; color: var(--pw-color, #6C5CE7); }

        /* Home observatory */
        .poa-home {
          display: flex;
          flex-direction: column;
          gap: 14px;
          width: min(100%, 430px);
          margin: 0 auto;
        }
        .poa-home-orbit {
          position: relative;
          overflow: hidden;
          min-height: clamp(430px, 118vw, 520px);
          border-radius: 28px;
          padding: 22px 18px 18px;
          border: 1px solid rgba(148,163,184,0.2);
          background-size: auto 100%;
          background-position: center center;
          box-shadow: 0 24px 70px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08);
        }
        .poa-home-orbit::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(circle at 50% 48%, rgba(232,215,167,0.08), transparent 28%),
            linear-gradient(180deg, rgba(2,6,23,0.02), rgba(2,6,23,0.44) 72%, rgba(2,6,23,0.86));
          pointer-events: none;
        }
        .poa-home-orbit > * { position: relative; z-index: 1; }
        .poa-home-copy span,
        .poa-home-section-head span {
          display: block;
          color: #e8d7a7;
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }
        .poa-home-copy h2 {
          margin: 5px 0 7px;
          color: #fff7db;
          font-size: 25px;
          line-height: 1.1;
          letter-spacing: 0.08em;
          text-shadow: 0 0 24px rgba(232,215,167,0.34);
        }
        .poa-home-copy p {
          margin: 0;
          max-width: 290px;
          color: rgba(203,213,225,0.72);
          font-size: 12px;
          line-height: 1.55;
        }
        .poa-home-astrolabe {
          width: clamp(170px, 52vw, 224px);
          height: clamp(170px, 52vw, 224px);
          margin: clamp(32px, 10vw, 42px) auto clamp(24px, 8vw, 34px);
          border-radius: 50%;
          display: grid;
          place-items: center;
          align-content: center;
          background: radial-gradient(circle at center, rgba(2,6,23,0.54) 0 38%, transparent 60%);
          border: 0;
          box-shadow: none;
        }
        .poa-home-astrolabe::before {
          content: none;
        }
        .poa-home-astrolabe strong {
          color: #f8fafc;
          font-size: 46px;
          line-height: 1;
          z-index: 1;
          text-shadow: 0 0 20px rgba(232,215,167,0.65), 0 0 48px rgba(103,232,249,0.26);
        }
        .poa-home-astrolabe span {
          color: rgba(203,213,225,0.66);
          font-size: 11px;
          margin-top: 4px;
          z-index: 1;
        }
        .poa-home-stats {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 1px;
          overflow: hidden;
          border-radius: 18px;
          border: 1px solid rgba(148,163,184,0.18);
          background: rgba(15,23,42,0.72);
          backdrop-filter: blur(14px);
        }
        .poa-home-stats div {
          min-width: 0;
          padding: 11px 6px;
          text-align: center;
          background: rgba(255,255,255,0.025);
        }
        .poa-home-stats strong {
          display: block;
          color: #f8fafc;
          font-size: 16px;
          line-height: 1.15;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .poa-home-stats span {
          display: block;
          margin-top: 4px;
          color: rgba(148,163,184,0.72);
          font-size: 10px;
        }
        .poa-home-focus {
          width: 100%;
          margin-top: 12px;
          padding: 12px 14px;
          display: grid;
          grid-template-columns: auto 1fr auto;
          gap: 10px;
          align-items: center;
          text-align: left;
          color: #e5e7eb;
          background: linear-gradient(135deg, color-mix(in srgb, var(--pw-color, #67e8f9) 18%, rgba(15,23,42,0.78)), rgba(2,6,23,0.72));
          border: 1px solid color-mix(in srgb, var(--pw-color, #67e8f9) 44%, transparent);
          border-radius: 16px;
          font-family: inherit;
          cursor: pointer;
        }
        .poa-home-focus span,
        .poa-home-focus em {
          color: rgba(203,213,225,0.72);
          font-size: 11px;
          font-style: normal;
          white-space: nowrap;
        }
        .poa-home-focus strong {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #f8fafc;
          font-size: 13px;
        }
        .poa-home-section-head {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 12px;
          padding: 2px 2px 0;
        }
        .poa-home-section-head h3 {
          margin: 4px 0 0;
          color: #f8fafc;
          font-size: 18px;
          letter-spacing: 0.02em;
        }
        .poa-home-section-head strong {
          color: #67e8f9;
          font-size: 18px;
        }
        .poa-home-rail {
          gap: 10px;
          padding-top: 0;
        }
        .poa-home-path-row {
          display: grid;
          grid-template-columns: 84px minmax(0, 1fr) 52px;
          gap: 13px;
          align-items: center;
          min-height: 112px;
          padding: 16px 14px 16px 18px;
          border-left: 0;
          border-radius: 10px;
          background-image:
            linear-gradient(90deg, rgba(2,6,23,0.2), rgba(2,6,23,0.5)),
            url(${pathwayRowFrame});
          background-size: 100% 100%;
          background-position: center;
        }
        .poa-home-path-row::after {
          content: '';
          position: absolute;
          left: 60px;
          top: 58px;
          bottom: -18px;
          width: 1px;
          background: linear-gradient(180deg, var(--pw-color, #67e8f9), transparent);
          opacity: 0.34;
        }
        .poa-home-path-orb {
          width: 78px;
          height: 78px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          box-shadow: 0 0 26px color-mix(in srgb, var(--pw-color, #67e8f9) 34%, transparent);
        }
        .poa-home-path-main { min-width: 0; }
        .poa-home-path-title {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }
        .poa-home-path-title strong {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #f8fafc;
          font-size: 14px;
        }
        .poa-home-path-title em {
          flex: 0 0 auto;
          color: #f8e7b6;
          font-size: 10px;
          font-style: normal;
          padding: 2px 7px;
          border-radius: 999px;
          border: 1px solid rgba(232,215,167,0.26);
          background: rgba(232,215,167,0.08);
        }
        .poa-home-path-meta {
          display: flex;
          gap: 7px;
          margin-top: 5px;
          min-width: 0;
          color: rgba(148,163,184,0.74);
          font-size: 10px;
        }
        .poa-home-path-meta span {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .poa-home-path-bars {
          display: grid;
          gap: 5px;
          margin-top: 9px;
        }
        .poa-home-path-bars i {
          display: block;
          height: 4px;
          overflow: hidden;
          border-radius: 999px;
          background: rgba(148,163,184,0.14);
        }
        .poa-home-path-bars b {
          display: block;
          height: 100%;
          border-radius: inherit;
        }
        .poa-home-path-action {
          text-align: right;
        }
        .poa-home-path-action span {
          display: block;
          color: rgba(103,232,249,0.86);
          font-size: 11px;
          font-weight: 800;
        }
        .poa-home-path-action b {
          display: inline-block;
          margin-top: 7px;
          color: #fff7db;
          font-size: 12px;
        }
        .poa-home-path-card {
          border-radius: 18px;
          padding: 16px;
          background-image:
            linear-gradient(180deg, rgba(2,6,23,0.1), rgba(2,6,23,0.58)),
            url(${pathwayRowFrame});
          background-size: 140% 100%;
          background-position: center;
        }

        .poa-emblem {
          background-image: url(${pathwayEmblems});
          background-repeat: no-repeat;
          background-size: 305% 205%;
          color: transparent;
          overflow: hidden;
        }
        .poa-emblem.emblem-0 { background-position: 1% 2%; }
        .poa-emblem.emblem-1 { background-position: 50% 2%; }
        .poa-emblem.emblem-2 { background-position: 99% 2%; }
        .poa-emblem.emblem-3 { background-position: 1% 98%; }
        .poa-emblem.emblem-4 { background-position: 50% 98%; }
        .poa-emblem.emblem-5 { background-position: 99% 98%; }

        @media (min-width: 760px) {
          .poa-home {
            width: 100%;
            max-width: 1180px;
            gap: 18px;
          }
          .poa-home-orbit {
            min-height: 430px;
            padding: 28px 28px 24px;
            background-size: 820px auto;
            background-repeat: no-repeat;
            background-position: center 45%;
          }
          .poa-home-copy p {
            max-width: 360px;
            font-size: 13px;
          }
          .poa-home-astrolabe {
            width: 180px;
            height: 180px;
            margin: 36px auto 34px;
          }
          .poa-home-stats,
          .poa-home-focus {
            width: calc(100% - 36px);
            margin-left: auto;
            margin-right: auto;
          }
          .poa-home-path-row {
            grid-template-columns: 92px minmax(0, 1fr) 72px;
            min-height: 118px;
            padding: 18px 24px;
          }
          .poa-home-path-row::after {
            left: 65px;
          }
          .poa-home-path-orb {
            width: 82px;
            height: 82px;
          }
          .poa-home-path-title strong {
            font-size: 16px;
          }
          .poa-home-path-meta {
            font-size: 11px;
          }
          .poa-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }

        @media (min-width: 1200px) {
          .poa-home {
            max-width: 1280px;
          }
          .poa-home-orbit {
            background-size: 880px auto;
          }
        }

        /* Detail subscribe button */
        .poa-detail-subscribe-btn {
          display: block; width: 100%; margin-bottom: 24px;
          padding: 14px 0; border-radius: 12px;
          border: 2px dashed rgba(108,92,231,0.4);
          background: rgba(108,92,231,0.06); color: #a29bfe;
          font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit;
          transition: all 0.2s;
        }
        .poa-detail-subscribe-btn:active { transform: scale(0.98); }
        .poa-detail-subscribe-btn.subscribed {
          border-style: solid; border-color: rgba(0,184,148,0.4);
          background: rgba(0,184,148,0.08); color: #00b894;
        }
        .poa-detail-subscribe-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Weekly progress bar */
        .poa-weekly-progress-bar {
          height: 6px; background: rgba(255,255,255,0.06); border-radius: 3px; overflow: hidden;
          margin-bottom: 4px;
        }
        .poa-weekly-progress-fill {
          height: 100%; border-radius: 3px; transition: width 0.5s ease;
        }

        /* Back button */
        .poa-back {
          background: none; border: none; color: var(--primary, #6C5CE7);
          font-size: 14px; font-weight: 600; cursor: pointer; padding: 8px 0;
          margin-bottom: 12px; font-family: inherit;
        }

        /* Detail Hero */
        .poa-detail-hero {
          border-radius: 16px; padding: 28px 20px; color: white;
          text-align: center; margin-bottom: 20px;
        }
        .poa-detail-icon { font-size: 48px; display: block; margin-bottom: 8px; }
        .poa-detail-hero h2 { margin: 0 0 8px; font-size: 20px; }
        .poa-detail-hero p { margin: 0 0 20px; font-size: 13px; opacity: 0.9; }
        .poa-detail-stats {
          display: flex; justify-content: space-around; gap: 8px;
        }
        .poa-stat { display: flex; flex-direction: column; align-items: center; }
        .poa-stat-value { font-size: 20px; font-weight: 700; }
        .poa-stat-label { font-size: 11px; opacity: 0.8; margin-top: 2px; }

        /* Actions */
        .poa-actions-row {
          display: flex; gap: 12px; margin-bottom: 24px;
        }
        .poa-action-btn {
          flex: 1; padding: 14px 16px; border: none; border-radius: 12px;
          font-size: 14px; font-weight: 600; cursor: pointer;
          font-family: inherit; transition: all 0.2s;
        }
        .poa-action-btn:active { transform: scale(0.97); }
        .poa-action-btn.primary {
          background: var(--primary, #6C5CE7); color: white;
        }
        .poa-action-btn.disabled,
        .poa-action-btn:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
        .poa-action-btn.secondary {
          background: var(--surface-solid, #1E293B); color: var(--text-primary, #E2E8F0);
          border: 1px solid var(--border, rgba(71,85,105,0.5));
        }

        /* Section title */
        .poa-section-title {
          font-size: 18px; font-weight: 700; margin: 0 0 16px;
          color: var(--text-primary, #E2E8F0);
        }

        /* Sequence list */
        .poa-sequence-list { display: flex; flex-direction: column; gap: 0; }
        .poa-seq-item {
          display: flex; align-items: flex-start; gap: 16px;
          padding: 16px 0; border-bottom: 1px solid var(--border-light, rgba(51,65,85,0.5));
          position: relative;
        }
        .poa-seq-item.locked { opacity: 0.4; }
        .poa-seq-marker {
          width: 36px; height: 36px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; font-weight: 700; color: white; flex-shrink: 0;
        }
        .poa-seq-info { flex: 1; min-width: 0; }
        .poa-seq-header { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .poa-seq-name { font-size: 14px; font-weight: 600; color: var(--text-primary, #E2E8F0); }
        .poa-seq-badge {
          padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600;
          background: #FFD700; color: #333;
        }
        .poa-seq-badge.passed { background: #00b894; color: white; }
        .poa-seq-desc {
          margin: 4px 0 0; font-size: 12px; color: var(--text-secondary, #94A3B8);
        }
        .poa-seq-meta {
          display: flex; gap: 16px; margin-top: 6px;
          font-size: 11px; color: var(--text-tertiary, #64748B);
        }
        .poa-seq-action {
          padding: 8px 16px; border: 2px solid var(--primary, #6C5CE7);
          border-radius: 8px; background: none; color: var(--primary, #6C5CE7);
          font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit;
          flex-shrink: 0; align-self: center;
        }
        .poa-seq-action:disabled {
          opacity: 0.45; cursor: not-allowed;
        }
        .poa-seq-action:active { background: var(--primary, #6C5CE7); color: white; }

        /* Star atlas detail */
        .poa-star-detail {
          margin: -12px -16px 0;
          padding: 0 10px 28px;
          background:
            radial-gradient(circle at 50% 14%, rgba(124,58,237,0.24), transparent 36%),
            linear-gradient(180deg, rgba(2,6,23,0.12), #020617 52%, #020617 100%);
          color: #e5e7eb;
        }
        .poa-star-atlas {
          position: relative;
          min-height: 760px;
          border: 1px solid rgba(148, 163, 184, 0.18);
          border-radius: 0 0 34px 34px;
          overflow: hidden;
          padding: 26px 18px 20px;
          background-size: cover;
          background-position: center top;
          box-shadow: 0 30px 90px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08);
        }
        .poa-star-atlas::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(circle at 70% 14%, rgba(232,215,167,0.28), transparent 7%),
            radial-gradient(circle at 42% 46%, rgba(103,232,249,0.2), transparent 16%),
            radial-gradient(circle at 50% 82%, rgba(167,139,250,0.24), transparent 18%);
          pointer-events: none;
        }
        .poa-star-atlas::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(2,6,23,0.08), rgba(2,6,23,0.18) 44%, rgba(2,6,23,0.94));
          pointer-events: none;
        }
        .poa-star-atlas > * { position: relative; z-index: 1; }
        .poa-star-topline {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          align-items: center;
        }
        .poa-star-kicker {
          color: #e8d7a7;
          font-size: 12px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          font-weight: 800;
          margin-bottom: 5px;
        }
        .poa-star-topline h2 {
          margin: 0;
          font-size: 24px;
          line-height: 1.15;
          letter-spacing: 0.04em;
          color: #fff7db;
          text-shadow: 0 0 20px rgba(232,215,167,0.4);
        }
        .poa-star-subscribe {
          border: 1px solid rgba(232, 215, 167, 0.38);
          background: rgba(15, 23, 42, 0.5);
          color: #f8e7b6;
          border-radius: 999px;
          padding: 8px 12px;
          font-size: 12px;
          font-weight: 800;
        }
        .poa-star-subscribe.subscribed {
          border-color: rgba(34, 211, 238, 0.48);
          color: #67e8f9;
          background: rgba(8, 47, 73, 0.35);
        }
        .poa-star-identity {
          margin-top: 20px;
          width: 100%;
          display: grid;
          grid-template-columns: 68px minmax(0, 1fr);
          gap: 12px 14px;
          align-items: center;
          padding: 14px;
          background: linear-gradient(135deg, rgba(15,23,42,0.68), rgba(30,27,75,0.48));
          border: 1px solid rgba(167,139,250,0.34);
          border-radius: 16px;
          backdrop-filter: blur(14px);
          box-shadow: 0 16px 38px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.08);
        }
        .poa-star-orb {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          color: #f5f3ff;
          font-size: 32px;
          font-weight: 900;
          background: radial-gradient(circle, rgba(168,85,247,0.96), rgba(30,27,75,0.72));
          border: 1px solid rgba(216,180,254,0.72);
          box-shadow: 0 0 28px rgba(168,85,247,0.62);
        }
        .poa-star-identity-copy { min-width: 0; }
        .poa-star-identity-copy > span {
          display: block;
          color: rgba(203,213,225,0.68);
          font-size: 11px;
          margin-bottom: 3px;
        }
        .poa-star-identity-copy > strong {
          display: block;
          color: #f8fafc;
          font-size: 16px;
        }
        .poa-star-identity-copy > p {
          margin: 4px 0 0;
          color: rgba(203,213,225,0.68);
          font-size: 11px;
          line-height: 1.45;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .poa-star-exp span {
          color: rgba(203,213,225,0.68);
          font-size: 11px;
        }
        .poa-star-exp {
          grid-column: 1 / -1;
          display: flex;
          flex-direction: column;
          gap: 7px;
          padding-top: 11px;
          border-top: 1px solid rgba(148,163,184,0.14);
        }
        .poa-star-exp strong {
          color: #fff7db;
          font-size: 16px;
        }
        .poa-star-exp-bar {
          height: 6px;
          border-radius: 999px;
          overflow: hidden;
          background: rgba(148,163,184,0.16);
        }
        .poa-star-exp-bar i {
          display: block;
          height: 100%;
          border-radius: inherit;
          box-shadow: 0 0 14px currentColor;
        }
        .poa-star-map {
          position: relative;
          height: 430px;
          margin-top: 10px;
        }
        .poa-star-map-heading {
          position: absolute;
          left: 0;
          top: 4px;
          z-index: 2;
          max-width: 150px;
        }
        .poa-star-map-heading span,
        .poa-star-map-heading strong { display: block; }
        .poa-star-map-heading span {
          color: #e8d7a7;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .12em;
        }
        .poa-star-map-heading strong {
          margin-top: 4px;
          color: rgba(226,232,240,.68);
          font-size: 11px;
          line-height: 1.4;
        }
        .poa-star-trail {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: visible;
        }
        .poa-star-trail-line {
          stroke-width: 0.9;
          stroke-linejoin: round;
          stroke-linecap: round;
          stroke-dasharray: 0.3 3.2;
          filter: drop-shadow(0 0 1.2px rgba(103,232,249,0.8));
          animation: poaTrailFlow 14s linear infinite;
        }
        .poa-star-trail-desktop { display: none; }
        @keyframes poaTrailFlow {
          to { stroke-dashoffset: -120; }
        }
        .poa-star-node {
          position: absolute;
          left: var(--mx);
          top: var(--my);
          transform: translate(-50%, -50%);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 1px solid rgba(148,163,184,0.4);
          background: radial-gradient(circle at 32% 28%, rgba(148,163,184,0.4), rgba(15,23,42,0.92) 70%);
          color: rgba(226,232,240,0.7);
          display: grid;
          place-items: center;
          font-weight: 900;
          font-size: 15px;
          box-shadow: 0 0 0 4px rgba(2,6,23,0.5), 0 6px 18px rgba(0,0,0,0.4);
          cursor: default;
        }
        .poa-star-node:disabled { cursor: default; }
        .poa-star-node::before {
          content: '';
          position: absolute;
          inset: -8px;
          border-radius: inherit;
          border: 1px solid rgba(255,255,255,0.07);
          opacity: 0.7;
        }
        .poa-star-node span { position: relative; z-index: 1; }
        .poa-star-node.completed {
          border-color: rgba(103,232,249,0.8);
          color: #a5f3fc;
          background: radial-gradient(circle at 32% 28%, rgba(165,243,252,0.55), rgba(8,47,73,0.92) 68%);
          box-shadow: 0 0 18px rgba(34,211,238,0.6), 0 0 0 4px rgba(8,47,73,0.4);
        }
        .poa-star-node.current {
          width: 80px;
          height: 80px;
          border: 2px solid rgba(248,231,182,0.9);
          background: radial-gradient(circle at 34% 26%, #ffffff, var(--pw-color, #a855f7) 30%, rgba(45,16,84,0.94) 80%);
          color: #fff;
          font-size: 32px;
          cursor: pointer;
          z-index: 3;
          box-shadow:
            0 0 48px rgba(192,132,252,0.95),
            0 0 90px rgba(168,85,247,0.55),
            0 0 0 9px rgba(88,28,135,0.32),
            inset 0 2px 4px rgba(255,255,255,0.4);
          animation: poaCurrentPulse 2.6s ease-in-out infinite;
        }
        .poa-star-node.current:disabled { cursor: default; }
        @keyframes poaCurrentPulse {
          0%, 100% { box-shadow: 0 0 44px rgba(192,132,252,0.85), 0 0 80px rgba(168,85,247,0.45), 0 0 0 9px rgba(88,28,135,0.3), inset 0 2px 4px rgba(255,255,255,0.4); }
          50% { box-shadow: 0 0 60px rgba(192,132,252,1), 0 0 120px rgba(168,85,247,0.7), 0 0 0 13px rgba(88,28,135,0.26), inset 0 2px 4px rgba(255,255,255,0.5); }
        }
        .poa-star-node-tag {
          position: absolute;
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          white-space: nowrap;
          font-style: normal;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.04em;
          color: #fdf3c7;
          padding: 3px 10px;
          border-radius: 999px;
          background: rgba(20,12,46,0.78);
          border: 1px solid rgba(248,231,182,0.4);
          box-shadow: 0 6px 16px rgba(0,0,0,0.4);
        }
        .poa-star-node.locked {
          opacity: 0.4;
        }
        .poa-star-next-card {
          position: absolute;
          right: 2px;
          top: 4px;
          width: 158px;
          padding: 10px 12px;
          border-radius: 14px;
          background: rgba(15,23,42,0.74);
          border: 1px solid rgba(232,215,167,0.36);
          box-shadow: 0 12px 30px rgba(0,0,0,0.34);
          backdrop-filter: blur(12px);
        }
        .poa-star-next-card strong {
          display: block;
          color: #f8e7b6;
          font-size: 12px;
          margin-bottom: 4px;
        }
        .poa-star-next-card small {
          display: block;
          color: rgba(203,213,225,0.58);
          font-size: 10px;
          margin-bottom: 4px;
          letter-spacing: .08em;
        }
        .poa-star-next-card span {
          color: rgba(226,232,240,0.72);
          font-size: 12px;
        }
        .poa-star-status {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          margin: -22px 12px 20px;
          position: relative;
          z-index: 2;
          background: linear-gradient(135deg, rgba(15,23,42,0.92), rgba(30,27,75,0.76));
          border: 1px solid rgba(148,163,184,0.2);
          border-radius: 22px;
          overflow: hidden;
          box-shadow: 0 20px 46px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.08);
          backdrop-filter: blur(16px);
        }
        .poa-star-week,
        .poa-star-exam {
          padding: 14px;
          min-width: 0;
          background: transparent;
          border: 0;
          color: inherit;
          text-align: left;
        }
        .poa-star-exam {
          border-left: 1px solid rgba(148,163,184,0.14);
        }
        .poa-star-week span,
        .poa-star-exam span {
          color: rgba(203,213,225,0.64);
          font-size: 12px;
        }
        .poa-star-week strong,
        .poa-star-exam strong {
          display: block;
          color: #f8fafc;
          font-size: 20px;
          margin-top: 4px;
        }
        .poa-star-exam.ready strong {
          color: #67e8f9;
        }
        .poa-star-week small,
        .poa-star-exam small {
          display: block;
          color: rgba(148,163,184,0.7);
          font-size: 11px;
          margin-top: 5px;
        }
        .poa-star-mini-bar {
          height: 4px;
          border-radius: 2px;
          background: rgba(148,163,184,0.16);
          overflow: hidden;
          margin-top: 10px;
        }
        .poa-star-mini-bar i {
          display: block;
          height: 100%;
          border-radius: inherit;
        }
        .poa-star-tasks,
        .poa-star-week-list {
          position: relative;
          overflow: hidden;
          margin: 0 2px 18px;
          padding: 16px;
          background: linear-gradient(180deg, rgba(15,23,42,0.92), rgba(15,23,42,0.78));
          border: 1px solid rgba(148,163,184,0.18);
          border-radius: 24px;
          box-shadow: 0 20px 48px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.06);
          backdrop-filter: blur(16px);
        }
        .poa-star-tasks::before,
        .poa-star-week-list::before {
          content: '';
          position: absolute;
          left: -22%;
          top: -40%;
          width: 70%;
          height: 80%;
          background: radial-gradient(circle, rgba(103,232,249,0.16), transparent 64%);
          pointer-events: none;
        }
        .poa-star-tasks > *,
        .poa-star-week-list > * { position: relative; z-index: 1; }
        .poa-star-tasks-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 14px;
        }
        .poa-star-tasks-head h3,
        .poa-star-week-list h3 {
          margin: 0;
          font-size: 22px;
          color: #fff7db;
        }
        .poa-star-tasks-head span {
          display: block;
          color: rgba(203,213,225,0.58);
          font-size: 12px;
          margin-top: 4px;
        }
        .poa-star-tasks-head strong {
          color: #f8e7b6;
          font-size: 13px;
          white-space: nowrap;
        }
        .poa-star-task-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .poa-star-task {
          display: grid;
          grid-template-columns: 46px 1fr auto;
          align-items: center;
          gap: 10px;
          padding: 12px;
          border: 1px solid rgba(148,163,184,0.12);
          border-radius: 16px;
          background: rgba(2,6,23,0.34);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
        }
        .poa-star-task:first-child { border-top: 1px solid rgba(148,163,184,0.12); }
        .poa-star-task.done { opacity: 0.72; }
        .poa-star-task-mark {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          border: 1px solid rgba(103,232,249,0.35);
          color: #67e8f9;
          background: rgba(8,47,73,0.28);
          box-shadow: 0 0 18px rgba(34,211,238,0.15);
          font-weight: 800;
        }
        .poa-star-task-title {
          color: #f8fafc;
          font-size: 14px;
          font-weight: 800;
        }
        .poa-star-task-body p {
          margin: 4px 0 0;
          color: rgba(203,213,225,0.68);
          font-size: 12px;
          line-height: 1.45;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .poa-star-task-meta {
          display: flex;
          gap: 8px;
          align-items: center;
          margin-top: 6px;
          font-size: 11px;
        }
        .poa-star-task-meta span {
          color: #c4b5fd;
          border: 1px solid rgba(167,139,250,0.32);
          border-radius: 999px;
          padding: 2px 7px;
        }
        .poa-star-task-meta b {
          color: #22d3ee;
        }
        .poa-star-task-action {
          min-width: 72px;
          border: 1px solid rgba(167,139,250,0.42);
          background: linear-gradient(135deg, #7c3aed, #9333ea);
          color: #fff;
          border-radius: 999px;
          padding: 9px 12px;
          font-size: 13px;
          font-weight: 800;
        }
        .poa-star-task-done {
          color: #34d399;
          font-size: 12px;
          font-weight: 800;
          white-space: nowrap;
        }
        .poa-star-task-foot {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid rgba(148,163,184,0.12);
          color: rgba(203,213,225,0.64);
          text-align: center;
          font-size: 12px;
        }
        .poa-star-week-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .poa-star-week-row {
          display: grid;
          grid-template-columns: 20px 1fr auto auto;
          gap: 8px;
          align-items: center;
          color: rgba(226,232,240,0.84);
          font-size: 12px;
        }
        .poa-star-week-row.done {
          color: #34d399;
        }
        .poa-star-week-row b {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
        .poa-star-week-row em {
          color: #22d3ee;
          font-style: normal;
        }
        .poa-star-week-row button {
          border: 1px solid rgba(148,163,184,0.22);
          background: rgba(30,41,59,0.78);
          color: #e5e7eb;
          border-radius: 8px;
          padding: 5px 8px;
          font-weight: 700;
        }

        /* 顶部 meta chips + 简介 */
        .poa-star-topline-main { min-width: 0; }
        .poa-star-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 10px;
        }
        .poa-star-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 800;
          border: 1px solid rgba(148,163,184,0.28);
          background: rgba(15,23,42,0.5);
          color: rgba(226,232,240,0.86);
          white-space: nowrap;
        }
        .poa-star-chip.popularity {
          color: #fda4af;
          border-color: rgba(244,114,182,0.32);
          background: rgba(244,114,182,0.12);
        }
        .poa-star-chip.followers {
          color: #a5b4fc;
          border-color: rgba(129,140,248,0.32);
          background: rgba(129,140,248,0.12);
        }
        .poa-star-intro {
          margin: 14px 0 0;
          color: rgba(203,213,225,0.78);
          font-size: 12.5px;
          line-height: 1.6;
        }

        /* 维度画像 */
        .poa-star-dimension {
          margin: 0 2px 18px;
          padding: 16px;
          background: linear-gradient(180deg, rgba(15,23,42,0.92), rgba(15,23,42,0.78));
          border: 1px solid rgba(148,163,184,0.18);
          border-radius: 24px;
          box-shadow: 0 20px 48px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.06);
          backdrop-filter: blur(16px);
        }
        .poa-star-dimension-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 14px;
        }
        .poa-star-dimension-head h3 {
          margin: 0;
          font-size: 18px;
          color: #fff7db;
        }
        .poa-star-dimension-head span {
          display: block;
          color: rgba(203,213,225,0.58);
          font-size: 11.5px;
          margin-top: 4px;
        }
        .poa-star-dimension-primary {
          flex-shrink: 0;
          font-size: 11.5px;
          font-weight: 800;
          white-space: nowrap;
        }
        .poa-star-dimension-list {
          display: flex;
          flex-direction: column;
          gap: 11px;
        }
        .poa-star-dimension-row {
          display: grid;
          grid-template-columns: 92px 1fr 40px;
          gap: 10px;
          align-items: center;
        }
        .poa-star-dimension-label {
          display: flex;
          align-items: center;
          gap: 6px;
          color: rgba(226,232,240,0.86);
          font-size: 12.5px;
          font-weight: 700;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .poa-star-dimension-label b { font-style: normal; }
        .poa-star-dimension-bar {
          height: 7px;
          border-radius: 4px;
          background: rgba(148,163,184,0.16);
          overflow: hidden;
        }
        .poa-star-dimension-bar i {
          display: block;
          height: 100%;
          border-radius: inherit;
          transition: width 0.6s ease;
        }
        .poa-star-dimension-row em {
          font-style: normal;
          text-align: right;
          font-size: 12px;
          font-weight: 800;
          color: rgba(226,232,240,0.7);
        }

        /* 晋升路线 */
        .poa-star-roadmap {
          margin: 0 2px 18px;
          padding: 16px;
          background: linear-gradient(180deg, rgba(15,23,42,0.92), rgba(15,23,42,0.78));
          border: 1px solid rgba(148,163,184,0.18);
          border-radius: 24px;
          box-shadow: 0 20px 48px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.06);
          backdrop-filter: blur(16px);
        }
        .poa-star-roadmap-head { margin-bottom: 12px; }
        .poa-star-roadmap-head h3 {
          margin: 0;
          font-size: 18px;
          color: #fff7db;
        }
        .poa-star-roadmap-head span {
          display: block;
          color: rgba(203,213,225,0.58);
          font-size: 11.5px;
          margin-top: 4px;
        }
        .poa-star-roadmap-list {
          display: flex;
          flex-direction: column;
        }
        .poa-star-roadmap-row {
          display: grid;
          grid-template-columns: 34px 1fr auto;
          gap: 12px;
          align-items: center;
          padding: 11px 0;
          border-top: 1px solid rgba(148,163,184,0.1);
        }
        .poa-star-roadmap-row:first-child { border-top: 0; }
        .poa-star-roadmap-row.locked { opacity: 0.5; }
        .poa-star-roadmap-row.current {
          margin: 4px -10px;
          padding: 11px 10px;
          border-radius: 14px;
          border-top: 0;
          background: rgba(124,58,237,0.14);
          border: 1px solid rgba(167,139,250,0.32);
        }
        .poa-star-roadmap-lv {
          width: 30px;
          height: 30px;
          border-radius: 9px;
          display: grid;
          place-items: center;
          color: #fff;
          font-size: 13px;
          font-weight: 900;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.3);
        }
        .poa-star-roadmap-body { min-width: 0; }
        .poa-star-roadmap-body strong {
          display: block;
          color: #f1f5f9;
          font-size: 13.5px;
          font-weight: 800;
        }
        .poa-star-roadmap-body p {
          margin: 3px 0 0;
          color: rgba(203,213,225,0.62);
          font-size: 11.5px;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .poa-star-roadmap-exp {
          flex-shrink: 0;
          font-size: 11px;
          font-weight: 800;
          color: rgba(148,163,184,0.82);
          white-space: nowrap;
        }
        .poa-star-roadmap-row.done .poa-star-roadmap-exp { color: #34d399; }
        .poa-star-roadmap-row.current .poa-star-roadmap-exp { color: #c4b5fd; }

        /* Quiz */
        .poa-quiz-header {
          border-radius: 16px; padding: 24px 20px; color: white;
          text-align: center; margin-bottom: 20px;
        }
        .poa-quiz-header h2 { margin: 0 0 6px; font-size: 20px; }
        .poa-quiz-header p { margin: 0; font-size: 13px; opacity: 0.9; }
        .poa-quiz-timer {
          margin-top: 12px;
          display: inline-flex;
          padding: 8px 14px;
          border-radius: 999px;
          background: rgba(255,255,255,0.14);
          color: white;
          font-size: 13px;
          font-weight: 700;
        }
        .poa-quiz-timer.warn { background: rgba(253,203,110,0.22); }
        .poa-quiz-timer.danger { background: rgba(225,112,85,0.28); }
        .poa-quiz-questions { display: flex; flex-direction: column; gap: 24px; }
        .poa-question {
          background: var(--surface-solid, #1E293B); border-radius: 16px;
          padding: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          border: 1px solid var(--border-light, rgba(51,65,85,0.5));
        }
        .poa-question-num {
          font-size: 12px; font-weight: 600; color: var(--primary, #6C5CE7);
          margin-bottom: 8px;
        }
        .poa-question-text {
          margin: 0 0 16px; font-size: 15px; font-weight: 600;
          color: var(--text-primary, #E2E8F0); line-height: 1.5;
        }
        .poa-options { display: flex; flex-direction: column; gap: 8px; }
        .poa-option {
          display: flex; align-items: center; gap: 12px;
          padding: 12px 16px; border: 2px solid var(--border, rgba(71,85,105,0.5));
          border-radius: 12px; background: var(--bg, #0F172A);
          cursor: pointer; transition: all 0.2s; font-family: inherit;
          text-align: left;
        }
        .poa-option:hover { border-color: rgba(139,92,246,0.5); background: var(--surface-hover, rgba(51,65,85,0.4)); }
        .poa-option.selected {
          border-color: var(--primary, #8B5CF6);
          background: rgba(139,92,246,0.15);
        }
        .poa-option-letter {
          width: 28px; height: 28px; border-radius: 50%;
          background: rgba(255,255,255,0.08); display: flex; align-items: center;
          justify-content: center; font-size: 13px; font-weight: 700;
          color: var(--text-secondary, #94A3B8); flex-shrink: 0;
        }
        .poa-option.selected .poa-option-letter {
          background: var(--primary, #6C5CE7); color: white;
        }
        .poa-option-text { font-size: 14px; color: var(--text-primary, #E2E8F0); }

        /* Submit button */
        .poa-submit-btn {
          width: 100%; padding: 16px; border: none; border-radius: 12px;
          background: var(--primary, #6C5CE7); color: white;
          font-size: 16px; font-weight: 700; cursor: pointer;
          font-family: inherit; margin-top: 24px; transition: all 0.2s;
        }
        .poa-submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .poa-submit-btn:not(:disabled):active { transform: scale(0.98); }

        /* Result */
        .poa-result {
          display: flex; justify-content: center; padding: 40px 0;
        }
        .poa-result-card {
          background: var(--surface-solid, #1E293B); border-radius: 20px;
          padding: 40px 28px; text-align: center; max-width: 400px; width: 100%;
          box-shadow: 0 4px 20px rgba(0,0,0,0.4);
          border: 1px solid var(--border-light, rgba(51,65,85,0.5));
        }
        .poa-result-icon { font-size: 64px; margin-bottom: 16px; }
        .poa-result-card h2 { margin: 0 0 12px; font-size: 24px; }
        .poa-result-card.passed h2 { color: #00b894; }
        .poa-result-card.failed h2 { color: #e17055; }
        .poa-result-score { font-size: 16px; color: var(--text-secondary, #94A3B8); margin: 0 0 16px; }
        .poa-result-score strong { font-size: 24px; color: var(--text-primary, #E2E8F0); }
        .poa-result-exp {
          display: inline-block; padding: 8px 20px; border-radius: 20px;
          background: linear-gradient(135deg, #6C5CE7, #a29bfe);
          color: white; font-size: 18px; font-weight: 700; margin-bottom: 16px;
        }
        .poa-result-upgrade {
          padding: 12px 20px; border-radius: 12px;
          background: linear-gradient(135deg, #ffeaa7, #fdcb6e);
          color: #333; font-size: 14px; margin-bottom: 16px;
        }
        .poa-result-hint {
          color: var(--text-secondary, #94A3B8); font-size: 14px; margin: 0 0 20px;
        }
        .poa-result-actions { display: flex; gap: 12px; }

        /* ========== Level-Up Animation ========== */
        @keyframes lu-fadeIn {
          from { opacity: 0; } to { opacity: 1; }
        }
        @keyframes lu-scaleIn {
          0% { transform: scale(0.5); opacity: 0; }
          60% { transform: scale(1.08); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes lu-particle-burst {
          0% { transform: translate(-50%, -50%) rotate(var(--angle)) translateX(0); opacity: 1; }
          100% { transform: translate(-50%, -50%) rotate(var(--angle)) translateX(var(--dist)); opacity: 0; }
        }
        @keyframes lu-ring-expand {
          0% { transform: scale(0); opacity: 0.9; }
          100% { transform: scale(3.5); opacity: 0; }
        }
        @keyframes lu-glow-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(255,215,0,0.3); }
          50% { box-shadow: 0 0 60px rgba(255,215,0,0.7), 0 0 120px rgba(255,215,0,0.3); }
        }
        @keyframes lu-slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes lu-level-old-out {
          0% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.5) translateY(-20px); }
        }
        @keyframes lu-level-new-in {
          0% { opacity: 0; transform: scale(2); }
          60% { transform: scale(0.9); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes lu-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }

        .lu-overlay {
          position: fixed; inset: 0; z-index: 10000;
          background: rgba(0,0,0,0.85);
          display: flex; align-items: center; justify-content: center;
          animation: lu-fadeIn 0.3s ease-out;
          backdrop-filter: blur(8px);
        }
        .lu-scene {
          position: relative;
          display: flex; flex-direction: column; align-items: center;
          padding: 48px 36px 36px;
          animation: lu-scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
          max-width: 380px; width: 90%;
        }

        .lu-particles {
          position: absolute; top: 50%; left: 50%;
          width: 0; height: 0; pointer-events: none;
        }
        .lu-particle {
          position: absolute; top: 0; left: 0;
          width: var(--size); height: var(--size);
          border-radius: 50%;
          background: hsl(var(--hue), 90%, 65%);
          animation: lu-particle-burst var(--dur) ease-out var(--delay) forwards;
          box-shadow: 0 0 6px hsl(var(--hue), 90%, 65%);
        }

        .lu-ring {
          position: absolute; top: 50%; left: 50%;
          width: 80px; height: 80px;
          margin: -40px 0 0 -40px;
          border-radius: 50%;
          border: 3px solid;
          opacity: 0; pointer-events: none;
        }
        .lu-ring-1 { animation: lu-ring-expand 1.2s ease-out 0.1s forwards; }
        .lu-ring-2 { animation: lu-ring-expand 1.2s ease-out 0.35s forwards; }

        .lu-icon {
          font-size: 72px;
          animation: lu-scaleIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s both,
                     lu-glow-pulse 2s ease-in-out 0.8s infinite;
          border-radius: 50%;
          margin-bottom: 12px;
          filter: drop-shadow(0 0 20px rgba(255,215,0,0.5));
        }

        .lu-level-wrap {
          display: flex; align-items: center; gap: 12px;
          margin-bottom: 8px;
          animation: lu-slideUp 0.5s ease-out 0.4s both;
        }
        .lu-level-old {
          font-size: 22px; font-weight: 700;
          color: var(--text-secondary, #94A3B8);
          animation: lu-level-old-out 0.6s ease-in 0.8s forwards;
          text-decoration: line-through; opacity: 0.6;
        }
        .lu-arrow {
          font-size: 24px; color: #FFD700;
          animation: lu-slideUp 0.3s ease-out 1s both;
        }
        .lu-level-new {
          font-size: 36px; font-weight: 900;
          animation: lu-level-new-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 1.1s both;
          text-shadow: 0 0 30px currentColor;
        }

        .lu-title {
          font-size: 28px; font-weight: 900; margin: 8px 0 16px;
          background: linear-gradient(90deg, #FFD700, #FFA500, #FFD700, #FFA500);
          background-size: 200% auto;
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: lu-slideUp 0.5s ease-out 0.5s both,
                     lu-shimmer 3s linear 1.5s infinite;
        }

        .lu-badge {
          padding: 10px 28px; border-radius: 999px;
          color: white; font-size: 16px; font-weight: 700;
          animation: lu-slideUp 0.5s ease-out 0.7s both;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          letter-spacing: 1px;
        }

        .lu-next-stage {
          margin-top: 28px;
          background: var(--surface-solid, rgba(30,41,59,0.95));
          border-radius: 16px; padding: 20px 24px;
          text-align: center; width: 100%;
          box-shadow: 0 4px 16px rgba(0,0,0,0.3);
          border: 1px solid var(--border-light, rgba(51,65,85,0.5));
          animation: lu-slideUp 0.6s ease-out 1.4s both;
        }
        .lu-next-label {
          font-size: 12px; font-weight: 600;
          color: var(--text-secondary, #94A3B8);
          text-transform: uppercase; letter-spacing: 2px;
          margin-bottom: 10px;
        }
        .lu-next-name {
          font-size: 20px; font-weight: 800; margin-bottom: 8px;
        }
        .lu-next-desc {
          font-size: 14px; line-height: 1.6;
          color: var(--text-secondary, #B0BEC5);
        }

        .lu-continue-btn {
          margin-top: 24px; padding: 14px 40px;
          border: none; border-radius: 999px;
          color: white; font-size: 16px; font-weight: 700;
          cursor: pointer; font-family: inherit;
          animation: lu-slideUp 0.5s ease-out 1.8s both;
          transition: transform 0.2s, box-shadow 0.2s;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        }
        .lu-continue-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 28px rgba(0,0,0,0.4);
        }
        .lu-continue-btn:active { transform: translateY(0); }

        /* Upgrade progress card */
        .poa-upgrade-card {
          background: var(--surface-solid, #1E293B);
          border: 1px solid var(--border-light, rgba(51,65,85,0.5));
          border-radius: 14px; padding: 16px;
          margin-bottom: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }
        .poa-upgrade-card.poa-upgrade-max {
          text-align: center; padding: 20px;
          background: linear-gradient(135deg, rgba(255,215,0,0.1), rgba(255,215,0,0.05));
          border-color: rgba(255,215,0,0.3);
          color: #ffd700; font-size: 15px; font-weight: 700;
        }
        .poa-upgrade-header {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 10px;
          font-size: 13px; color: var(--text-secondary, #94A3B8);
        }
        .poa-upgrade-header strong {
          color: var(--text-primary, #E2E8F0);
        }
        .poa-upgrade-remaining {
          font-weight: 700; color: #ffd700;
        }
        .poa-upgrade-remaining.ready {
          color: #00e0a4;
        }
        .poa-upgrade-bar {
          height: 8px; background: rgba(255,255,255,0.06);
          border-radius: 4px; overflow: hidden; margin-bottom: 8px;
        }
        .poa-upgrade-bar-fill {
          height: 100%; border-radius: 4px;
          transition: width 0.6s ease;
        }
        .poa-upgrade-meta {
          display: flex; justify-content: space-between;
          font-size: 11px; color: var(--text-tertiary, #64748B);
        }

        /* Daily task summary */
        .poa-daily-summary {
          margin-bottom: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--border-light, rgba(51,65,85,0.3));
        }
        .poa-daily-summary-top {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 8px;
        }
        .poa-daily-summary-done {
          font-size: 14px; font-weight: 700;
          color: var(--text-primary, #E2E8F0);
        }
        .poa-daily-summary-exp {
          font-size: 12px; font-weight: 600; color: #00b894;
        }
        .poa-daily-summary-bar {
          height: 6px; background: rgba(255,255,255,0.06);
          border-radius: 3px; overflow: hidden;
        }
        .poa-daily-summary-fill {
          height: 100%; border-radius: 3px;
          transition: width 0.5s ease;
        }
        .poa-daily-badge {
          margin-top: 10px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 700;
          padding: 6px 10px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.12);
          background: rgba(255,255,255,0.04);
        }

        /* Daily tasks */
        .poa-daily-card {
          background: var(--surface-solid, #1E293B);
          border: 1px solid var(--border-light, rgba(51,65,85,0.5));
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 24px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }
        .poa-daily-meta {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          font-size: 12px;
          color: var(--text-tertiary, #64748B);
          margin-bottom: 12px;
        }
        .poa-daily-list { display: flex; flex-direction: column; gap: 10px; }
        .poa-daily-item {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          padding: 12px;
          border-radius: 12px;
          background: var(--bg, #0F172A);
          border: 1px solid var(--border, rgba(71,85,105,0.5));
        }
        .poa-daily-item.done { opacity: 0.7; }
        .poa-daily-item-title {
          font-size: 14px;
          font-weight: 700;
          color: var(--text-primary, #E2E8F0);
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
        }
        .poa-daily-exp {
          font-size: 12px;
          font-weight: 700;
          color: #00b894;
          background: rgba(0,184,148,0.12);
          padding: 2px 8px;
          border-radius: 999px;
        }
        .poa-daily-item-desc {
          margin-top: 6px;
          font-size: 12px;
          color: var(--text-secondary, #94A3B8);
          line-height: 1.5;
        }
        .poa-daily-item-cat {
          margin-top: 6px;
          font-size: 11px;
          color: var(--text-tertiary, #64748B);
        }
        .poa-daily-done {
          padding: 8px 12px;
          border-radius: 10px;
          border: none;
          background: var(--primary, #6C5CE7);
          color: white;
          font-weight: 700;
          cursor: pointer;
          flex-shrink: 0;
        }
        .poa-daily-done:disabled { opacity: 0.6; cursor: not-allowed; }
        .poa-daily-done-badge {
          display: inline-flex;
          align-items: center;
          height: 32px;
          padding: 0 10px;
          border-radius: 999px;
          background: rgba(0,184,148,0.15);
          color: #00b894;
          font-size: 12px;
          font-weight: 700;
        }
        .poa-daily-loading,
        .poa-daily-empty {
          font-size: 13px;
          color: var(--text-secondary, #94A3B8);
        }

        /* ===== Header User Info ===== */
        .poa-header-user {
          display: flex; align-items: center; gap: 12px;
        }
        .poa-header-avatar {
          width: 42px; height: 42px; border-radius: 50%;
          background: linear-gradient(135deg, #8B5CF6, #6366F1);
          display: flex; align-items: center; justify-content: center;
          color: white; font-size: 18px; font-weight: 800;
          border: 2px solid rgba(255,255,255,0.3);
          box-shadow: 0 0 12px rgba(139,92,246,0.4);
          flex-shrink: 0;
        }
        .poa-header-san-badge {
          width: 42px; height: 42px; border-radius: 50%;
          background: linear-gradient(135deg, #f43f5e, #e11d48);
          display: flex; align-items: center; justify-content: center;
          color: white; font-size: 11px; font-weight: 800;
          letter-spacing: 0.5px;
          box-shadow: 0 0 12px rgba(244,63,94,0.4);
        }
        .poa-header-user-info {
          display: flex; flex-direction: column; gap: 2px;
        }
        .poa-header-title {
          color: white; font-size: 15px; font-weight: 600;
          letter-spacing: 1px;
        }
        .poa-header-title strong { font-weight: 800; }
        .poa-header-sub {
          color: rgba(255,255,255,0.6); font-size: 11px;
        }

        /* ===== Header Progress Bars ===== */
        .poa-header-bars {
          position: relative; z-index: 1;
          display: flex; gap: 8px;
          padding: 0 20px 20px;
        }
        .poa-hbar {
          flex: 1; display: flex; align-items: center; gap: 6px;
        }
        .poa-hbar-label {
          font-size: 9px; font-weight: 800; letter-spacing: 1px;
          min-width: 32px;
        }
        .poa-hbar-track {
          flex: 1; height: 6px;
          background: rgba(30,41,59,0.8);
          border-radius: 3px; overflow: hidden;
        }
        .poa-hbar-fill {
          height: 100%; border-radius: 3px;
          transition: width 0.6s ease;
        }
        .poa-hbar-pct {
          font-size: 10px; font-weight: 700;
          color: rgba(255,255,255,0.7);
          min-width: 28px; text-align: right;
        }

        /* Detail page responsive composition */
        .poa-star-atlas {
          background-size: cover;
          background-position: center;
        }
        .poa-star-detail > .poa-star-dimension,
        .poa-star-detail > .poa-star-tasks,
        .poa-star-detail > .poa-star-week-list {
          min-width: 0;
        }
        @media (max-width: 430px) {
          .poa-star-detail { padding-inline: 8px; }
          .poa-star-atlas {
            min-height: 690px;
            padding: 20px 14px 16px;
            border-radius: 0 0 22px 22px;
          }
          .poa-star-kicker { font-size: 10px; letter-spacing: .14em; }
          .poa-star-topline h2 { font-size: 22px; }
          .poa-star-subscribe { padding: 7px 10px; }
          .poa-star-chips { gap: 5px; margin-top: 8px; }
          .poa-star-chip { padding: 3px 7px; font-size: 10px; }
          .poa-star-intro { margin-top: 10px; line-height: 1.5; }
          .poa-star-identity { margin-top: 14px; }
          .poa-star-map { height: 390px; margin-top: 8px; }
          .poa-star-map-heading { max-width: 126px; }
          .poa-star-map-heading strong { font-size: 10px; }
          .poa-star-next-card { width: 136px; padding: 8px 10px; }
          .poa-star-node.current { width: 66px; height: 66px; font-size: 27px; }
          .poa-star-node-tag { max-width: 112px; overflow: hidden; text-overflow: ellipsis; }
          .poa-star-status { margin-inline: 8px; border-radius: 16px; }
          .poa-star-week, .poa-star-exam { padding: 12px 10px; }
          .poa-star-task { grid-template-columns: 38px minmax(0,1fr); }
          .poa-star-task-mark { width: 36px; height: 36px; }
          .poa-star-task-action, .poa-star-task-done {
            grid-column: 2;
            justify-self: start;
          }
          .poa-star-task-action { min-width: 86px; }
          .poa-star-dimension-row { grid-template-columns: 76px minmax(0,1fr) 34px; gap: 8px; }
          .poa-star-week-row { grid-template-columns: 18px minmax(0,1fr) auto; }
          .poa-star-week-row button { grid-column: 2 / -1; justify-self: start; }
        }
        @media (min-width: 880px) {
          .poa-star-detail {
            display: grid;
            grid-template-columns: minmax(0, 1.65fr) minmax(300px, .75fr);
            column-gap: 20px;
            align-items: start;
            max-width: 1240px;
            margin: 0 auto;
            padding: 18px 20px 44px;
            background: transparent;
          }
          .poa-star-atlas {
            grid-column: 1;
            grid-row: 1 / span 4;
            min-height: 720px;
            border-radius: 18px;
            padding: 30px 30px 24px;
            position: sticky;
            top: 16px;
          }
          .poa-star-status {
            grid-column: 2;
            grid-row: 1;
            margin: 0 0 16px;
            border-radius: 16px;
          }
          .poa-star-detail > .poa-star-dimension {
            grid-column: 2;
            grid-row: 2;
          }
          .poa-star-detail > .poa-star-tasks {
            grid-column: 2;
            grid-row: 3;
          }
          .poa-star-detail > .poa-star-week-list {
            grid-column: 2;
            grid-row: 4;
          }
          .poa-star-identity {
            width: min(56%, 440px);
            margin-left: auto;
          }
          .poa-star-map {
            height: 420px;
            margin-top: 4px;
          }
          .poa-star-node {
            left: var(--dx);
            top: var(--dy);
          }
          .poa-star-trail-mobile { display: none; }
          .poa-star-trail-desktop { display: block; }
          .poa-star-next-card {
            left: 0;
            right: auto;
            top: 54px;
          }
          .poa-star-dimension,
          .poa-star-tasks,
          .poa-star-week-list {
            margin-bottom: 16px;
            border-radius: 16px;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .poa-star-trail-line,
          .poa-star-node.current { animation: none; }
        }

        /* Stable sequence rail shared by desktop and mobile detail views. */
        .poa-sequence-route { height: auto; min-height: 188px; margin-top: 30px; padding-top: 56px; }
        .poa-sequence-route .poa-star-map-heading { top: 0; max-width: none; }
        .poa-sequence-scroll { overflow-x: auto; padding: 12px 4px 10px; scrollbar-width: none; }
        .poa-sequence-scroll::-webkit-scrollbar { display: none; }
        .poa-sequence-track { position: relative; display: grid; grid-template-columns: repeat(10, minmax(58px, 1fr)); gap: 10px; min-width: 700px; }
        .poa-sequence-track::before { content: ''; position: absolute; left: 3%; right: 3%; top: 25px; height: 1px; background: linear-gradient(90deg, #67e8f9, rgba(148,163,184,.24)); }
        .poa-sequence-step { position: relative; z-index: 1; min-width: 0; text-align: center; }
        .poa-sequence-step .poa-star-node { position: relative; left: auto; top: auto; transform: none; width: 50px; height: 50px; margin: 0 auto 9px; }
        .poa-sequence-step.completed .poa-star-node { border-color: rgba(103,232,249,.8); color: #a5f3fc; background: #0b2534; box-shadow: 0 0 16px rgba(34,211,238,.35); }
        .poa-sequence-step.current .poa-star-node { width: 50px; height: 50px; border: 1px solid #f8e7b6; background: var(--pw-color, #8b5cf6); color: #fff; font-size: 18px; box-shadow: 0 0 24px color-mix(in srgb, var(--pw-color, #8b5cf6) 60%, transparent), 0 0 0 5px rgba(255,255,255,.07); animation: none; }
        .poa-sequence-step.locked .poa-star-node { opacity: .48; }
        .poa-sequence-step strong { display: block; overflow: hidden; color: #718096; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
        .poa-sequence-step.current strong { color: #fff7db; }
        .poa-sequence-route .poa-star-next-card { position: static; width: auto; margin-top: 12px; display: flex; align-items: center; gap: 9px; border-radius: 6px; }
        .poa-sequence-route .poa-star-next-card small,
        .poa-sequence-route .poa-star-next-card strong { margin: 0; }
        .poa-header-sigil { color: #e8d7a7; font-size: 18px; }
        .poa-verification { max-width: 760px; margin: 0 auto; }
        .poa-verification-hero { display: flex; align-items: center; gap: 18px; padding: 24px; border: 1px solid rgba(148,163,184,.18); border-radius: 8px; background: linear-gradient(110deg, color-mix(in srgb, var(--pw-color) 24%, #0f172a), #0f172a); }
        .poa-verification-hero > span { width: 58px; height: 58px; display: grid; place-items: center; flex: 0 0 auto; border: 1px solid rgba(255,255,255,.2); border-radius: 50%; background: rgba(2,6,23,.42); font-size: 27px; }
        .poa-verification-hero small { color: #c4b5fd; font-size: 11px; }
        .poa-verification-hero h2 { margin: 4px 0; color: #fff7db; font-size: 23px; }
        .poa-verification-hero p { margin: 0; color: #a7b3c6; font-size: 12px; }
        .poa-verification-card { margin-top: 14px; padding: 22px; border: 1px solid rgba(148,163,184,.18); border-radius: 8px; background: rgba(15,23,42,.92); }
        .poa-verification-progress strong { color: white; font-size: 35px; }
        .poa-verification-progress span { color: #94a3b8; }
        .poa-verification-bar { height: 6px; margin: 10px 0; overflow: hidden; border-radius: 3px; background: rgba(148,163,184,.16); }
        .poa-verification-bar i { display: block; height: 100%; }
        .poa-verification-card > p { color: #94a3b8; font-size: 12px; }
        .poa-verification-field { display: grid; gap: 9px; color: #e2e8f0; font-size: 12px; }
        .poa-verification-field textarea, .poa-verification-field input { width: 100%; box-sizing: border-box; border: 1px solid rgba(148,163,184,.22); border-radius: 6px; background: #080f20; color: white; padding: 12px; font: inherit; resize: vertical; }
        .poa-verification-field small { color: #8290a5; text-align: right; }
        @media (min-width: 880px) {
          .poa-main { max-width: 1280px; margin-left: auto; margin-right: auto; }
          .poa-star-atlas { min-height: 600px; border-radius: 8px; }
          .poa-sequence-route { height: auto; margin-top: 28px; }
          .poa-star-identity { width: min(70%, 520px); }
          .poa-star-status, .poa-star-dimension, .poa-star-tasks, .poa-star-week-list { border-radius: 8px; }
        }
        @media (max-width: 430px) {
          .poa-star-atlas { min-height: 0; border-radius: 0 0 8px 8px; }
          .poa-sequence-route { height: auto; min-height: 196px; }
          .poa-sequence-track { grid-template-columns: repeat(10, 58px); min-width: 670px; }
        }

      `}</style>
    </div>
  );
};

export default PathOfAscension;
